import React, { useState, useEffect } from 'react';
import { Account } from './services/Account';
import { Navigate, Link, NavLink, useNavigate, Outlet } from 'react-router-dom'; // Added Link, NavLink, useNavigate, Outlet
import './BuddyPage.css';
import { Street, Position, ActionType, Action as BaseAction, Hand, Player } from './types';
import { PlayFab } from './services/PlayFab';
import { AnalysisDisplay } from './components/AnalysisDisplay';

// Add export
export const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
// Add export
export const SUITS = [
  { symbol: '♣', name: 'c', color: 'black' },
  { symbol: '♥', name: 'h', color: 'red' },
  { symbol: '♦', name: 'd', color: 'red' },
  { symbol: '♠', name: 's', color: 'black' },
];

// Add these helper functions at the top
const POSITION_ORDER: Position[] = ['SB', 'BB', 'UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN'];

const getNextPosition = (currentPosition: Position): Position => {
  const currentIndex = POSITION_ORDER.indexOf(currentPosition);
  return POSITION_ORDER[(currentIndex + 1) % POSITION_ORDER.length];
};

// Card Picker Component
// Add export
export interface CardPickerProps {
  show: boolean;
  onSelect: (card: string) => void;
  onClose: () => void;
  hand: Hand;
}

// Add this helper function to check if a card is already used
// Add export
export const isCardUsed = (card: string, hand: Hand): boolean => {
  return Object.values(hand).includes(card);
};

// Add export
export const CardPicker: React.FC<CardPickerProps> = ({ show, onSelect, onClose, hand }) => {
  // Remove the state for the input value
  // const [inputValue, setInputValue] = useState('');

  // Remove the keyboard input handler function
  // const handleKeyboardInput = (input: string) => { ... };

  // Keep the dropdown handler
  const handleDropdownSelect = (event: React.ChangeEvent<HTMLSelectElement>, suitName: string) => {
    const rank = event.target.value;
    if (rank) { // Ensure a rank was selected (not the placeholder)
      const card = `${rank}${suitName}`;
      if (!isCardUsed(card, hand)) {
        onSelect(card);
      }
      // Reset dropdown to placeholder after selection
      event.target.value = "";
    }
  };

  if (!show) return null;

  return (
    <div className="card-picker-overlay">
      <div className="card-picker-modal">
        <h2 className="card-picker-title">Select a Card</h2>
        
        {/* REMOVE the Keyboard input field container and input */}
        {/* <div className="card-input-container">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleKeyboardInput(inputValue);
              }
            }}
            placeholder="Type card (e.g. Ah, 10c)"
            className="card-input"
            autoFocus
          />
        </div> */}

        {/* --- Grid Layout (for Desktop) --- */}
        <div className="card-picker-grid">
          <div className="card-picker-ranks">
            {RANKS.map(rank => (
              <div key={rank} className="rank-header">{rank}</div>
            ))}
          </div>
          {SUITS.map(suit => (
            <div key={suit.name} className="suit-row">
              {RANKS.map(rank => {
                const card = `${rank}${suit.name}`;
                const isUsed = isCardUsed(card, hand);
                return (
                  <button
                    key={card}
                    className={`card-button ${isUsed ? 'used' : ''}`}
                    style={{ color: suit.color }}
                    disabled={isUsed}
                    onClick={() => !isUsed && onSelect(card)}
                  >
                    {/* Use card-text span for consistency */}
                    <span className="card-text">{rank}{suit.symbol}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* --- Dropdown Layout (for Mobile) --- */}
        <div className="card-picker-dropdowns">
          {SUITS.map(suit => (
            <div key={suit.name} className="suit-dropdown-container">
              <span className="suit-label" style={{ color: suit.color }}>
                {suit.symbol}
              </span>
              <select 
                className="rank-select"
                value="" /* Controlled value, reset after selection */
                onChange={(e) => handleDropdownSelect(e, suit.name)}
              >
                <option value="" disabled>Select...</option> 
                {RANKS.map(rank => {
                  const card = `${rank}${suit.name}`;
                  const isUsed = isCardUsed(card, hand);
                  return (
                    <option key={card} value={rank} disabled={isUsed}>
                      {rank} {isUsed ? '(Used)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          ))}
        </div>

        <button className="close-button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

// Board Component
interface BoardProps {
  hand: Hand;
  onCardClick?: (cardKey: keyof Hand, value?: string) => void;  // Updated to accept a value
  readOnly?: boolean;
}

const Card: React.FC<{ card: string }> = ({ card }) => {
  if (!card) return (
    <div className="card" />
  );

  const rank = card[0];
  const suit = card[1];
  const color = suit === 'h' || suit === 'd' ? 'red' : 'black';
  const suitSymbol = {
    'c': '♣',
    'h': '♥',
    'd': '♦',
    's': '♠'
  }[suit];

  return (
    <div className="card filled">
      <span style={{ color, fontSize: '20px' }}>{rank}</span>
      <span style={{ color, fontSize: '24px' }}>{suitSymbol}</span>
    </div>
  );
};

const Board: React.FC<BoardProps> = ({ hand, onCardClick, readOnly = false }) => {
  const [holeCardsInput, setHoleCardsInput] = useState('');
  const [communityCardsInput, setCommunityCardsInput] = useState('');

  const handleHoleCardsInput = (input: string) => {
    // Remove spaces and convert to lowercase
    const cards = input.replace(/\s+/g, '').toLowerCase();
    if (cards.length === 4) { // Two cards, each 2 chars (e.g., "ah6c")
      const card1 = cards.slice(0, 2);
      const card2 = cards.slice(2, 4);
      
      // Convert suit to uppercase, keep rank as is
      const formattedCard1 = card1[0].toUpperCase() + card1[1];
      const formattedCard2 = card2[0].toUpperCase() + card2[1];
      
      // Validate and set cards
      if (isValidCard(formattedCard1) && isValidCard(formattedCard2)) {
        onCardClick?.('holeCard1', formattedCard1);
        onCardClick?.('holeCard2', formattedCard2);
        setHoleCardsInput('');
      }
    }
  };

  const handleCommunityCardsInput = (input: string) => {
    // Remove spaces and convert to lowercase
    const cards = input.replace(/\s+/g, '').toLowerCase();
    
    // Check if length is valid (6 for flop, 8 for turn, 10 for river)
    if (cards.length === 6 || cards.length === 8 || cards.length === 10) {
      const cardPairs = cards.match(/.{2}/g) || []; // Split into pairs of characters
      
      // Format each card (rank uppercase, suit lowercase)
      const formattedCards = cardPairs.map(card => 
        card[0].toUpperCase() + card[1]
      );
      
      // Validate all cards
      if (formattedCards.every(isValidCard)) {
        // Set cards based on how many were provided
        onCardClick?.('flopCard1', formattedCards[0]);
        onCardClick?.('flopCard2', formattedCards[1]);
        onCardClick?.('flopCard3', formattedCards[2]);
        
        if (formattedCards.length >= 4) {
          onCardClick?.('turnCard', formattedCards[3]);
        }
        
        if (formattedCards.length === 5) {
          onCardClick?.('riverCard', formattedCards[4]);
        }
        
        setCommunityCardsInput('');
      }
    }
  };

  const isValidCard = (card: string): boolean => {
    const rank = card[0];
    const suit = card[1].toLowerCase();
    return RANKS.includes(rank) && ['c', 'h', 'd', 's'].includes(suit);
  };

  return (
    <div className="board">
      <div className="hole-cards">
        <h3>Hole Cards</h3>
        <div className="flex">
          <div onClick={() => !readOnly && onCardClick?.('holeCard1')}>
            <Card card={hand.holeCard1} />
          </div>
          <div onClick={() => !readOnly && onCardClick?.('holeCard2')}>
            <Card card={hand.holeCard2} />
          </div>
        </div>
        <input
          type="text"
          value={holeCardsInput}
          onChange={(e) => setHoleCardsInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleHoleCardsInput(holeCardsInput);
            }
          }}
          placeholder="Type cards (e.g. AH6c)"
          className="cards-input"
        />
      </div>
      
      <div className="community-cards">
        <h3>Community Cards</h3>
        <div className="flex">
          <div onClick={() => !readOnly && onCardClick?.('flopCard1')}>
            <Card card={hand.flopCard1} />
          </div>
          <div onClick={() => !readOnly && onCardClick?.('flopCard2')}>
            <Card card={hand.flopCard2} />
          </div>
          <div onClick={() => !readOnly && onCardClick?.('flopCard3')}>
            <Card card={hand.flopCard3} />
          </div>
          <div onClick={() => !readOnly && onCardClick?.('turnCard')}>
            <Card card={hand.turnCard} />
          </div>
          <div onClick={() => !readOnly && onCardClick?.('riverCard')}>
            <Card card={hand.riverCard} />
          </div>
        </div>
        <input
          type="text"
          value={communityCardsInput}
          onChange={(e) => setCommunityCardsInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleCommunityCardsInput(communityCardsInput);
            }
          }}
          placeholder="Type 3-5 cards (e.g. AH6c2d or AH6c2dKs)"
          className="cards-input"
        />
      </div>
    </div>
  );
};

interface StreetPanelProps {
  street: Street;
  isOpen: boolean;
  onToggle: () => void;
  players: Player[];
  bets: Record<Street, number>;
  onAction: (playerId: number, action: ExtendedAction, street: Street) => void;
  onAddVillain: () => void;
  onRemoveVillain: (villainId: number) => void;
  onPositionChange: (playerId: number, position: Position) => void;
  getAvailablePositions: (players: Player[], currentPlayerId: number) => Position[];
}

const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river'];

// Move getOrderedPlayers outside the class and export it
export const getOrderedPlayers = (players: Player[], street: Street) => {
  // Define position order for preflop and postflop
  const preflopOrder = ['UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
  const postflopOrder = ['SB', 'BB', 'UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN'];  // SB first post-flop

  // Use appropriate order based on street
  const positionOrder = street === 'preflop' ? preflopOrder : postflopOrder;

  return [...players].sort((a, b) => {
    const posA = positionOrder.indexOf(a.position);
    const posB = positionOrder.indexOf(b.position);
    return posA - posB;
  });
};

// Update the ExtendedAction interface to not extend BaseAction
interface ExtendedAction {
  type: 'Check' | 'Fold' | 'Call' | 'Bet' | 'Raise' | 'Delete';
  player?: string;
  position?: string;
  actionIndex?: number;
  playerId?: number;
  amount?: number;
  isAllIn?: boolean;
}

// Keep only one declaration at the top of the file
const getActionLabel = (action: ExtendedAction, index: number, allActions: ExtendedAction[]): string => {
  switch (action.type) {
    case 'Check':
    case 'Fold':
      return `#${index + 1} ${action.type}`;
    case 'Bet':
      return `#${index + 1} Bet $${action.amount || 0}`;
    case 'Call':
      // Find the last bet/raise amount to use for the call
      const lastBet = [...allActions]
        .slice(0, index)
        .reverse()
        .find(a => a.type === 'Bet' || a.type === 'Raise');
      return `#${index + 1} Call $${lastBet?.amount || 0}`;
    case 'Raise':
      return `#${index + 1} Raise $${action.amount || 0}`;
    default:
      return `#${index + 1} ${action.type}`;
  }
};

// Update the BetInput interface to include the current street
interface BetInputProps {
  onConfirm: (amount: number) => void;  // Remove street parameter since it's handled by parent
  onCancel: () => void;
  currentStreet?: Street;  // Optional for backward compatibility
  // Add props for all-in (v16)
  effectiveStack: number; 
  onConfirmAllIn: () => void; 
}

// Update the BetInput component
const BetInput: React.FC<BetInputProps> = ({ onConfirm, onCancel, effectiveStack, onConfirmAllIn }) => {
  const [amount, setAmount] = React.useState('');

  return (
    <div className="bet-input-overlay">
      <div className="bet-input-container">
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Enter amount"
          autoFocus
        />
        <div className="bet-input-buttons">
          {/* Standard Confirm Button */}
          <button onClick={() => onConfirm(Number(amount))}>
            Confirm ${amount || '0'} 
          </button>
          {/* Add All-in Button (v16) */}
          <button onClick={onConfirmAllIn} className="all-in-confirm">
            All-in (${effectiveStack})
          </button>
          <button onClick={onCancel} className="cancel-bet">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Update the StreetPanel component
const StreetPanel: React.FC<StreetPanelProps> = ({
  street,
  isOpen,
  onToggle,
  players,
  bets,
  onAction,
  onAddVillain,
  onRemoveVillain,
  onPositionChange,
  getAvailablePositions
}) => {
  const [showBetInput, setShowBetInput] = React.useState(false);
  const [pendingAction, setPendingAction] = React.useState<{
    playerId: number;
    type: 'Bet' | 'Raise';
    street: Street;
  } | null>(null);
  const currentBet = bets[street];
  const orderedPlayers = getOrderedPlayers(players, street);

  // Get maximum number of actions across all players for this street
  const maxActions = Math.max(...orderedPlayers.map(p => p.actions[street].length));

  return (
    <div className="timeline-street-panel">
      <div className="timeline-header">
        <h3>{street.charAt(0).toUpperCase() + street.slice(1)}</h3>
        {street === 'preflop' && (
          <button onClick={onAddVillain} className="add-villain-btn">
            Add Villain
      </button>
        )}
      </div>

      <div className="timeline-container">
              {orderedPlayers.map(player => (
          <div key={player.id} className="timeline-row">
            <div className="player-info">
              <div className="player-name">{player.name}</div>
              {street === 'preflop' && (
                  <select 
                    value={player.position}
                    onChange={(e) => onPositionChange(player.id, e.target.value as Position)}
                  className="position-select"
                  >
                    {getAvailablePositions(players, player.id)
                      .concat(player.position)
                      .filter((pos, index, self) => self.indexOf(pos) === index)
                      .sort((a, b) => POSITION_ORDER.indexOf(a) - POSITION_ORDER.indexOf(b))
                      .map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))
                    }
                  </select>
              )}
                  {!player.isHero && (
                    <button 
                      onClick={() => onRemoveVillain(player.id)}
                  className="remove-villain-btn"
                    >
                      ✕
                    </button>
                  )}
                </div>

            <div className="timeline-actions">
              {/* Render existing actions */}
              {player.actions[street].map((action, index) => (
                <div 
                  key={index} 
                  className="timeline-dot"
                >
                  <span className="dot-number">{index + 1}</span>
                  <span className="action-label">
                    {action.type === 'Call' ? `Call $${action.amount}` :
                     action.type === 'Bet' ? `Bet $${action.amount}` :
                     action.type === 'Raise' ? `Raise $${action.amount}` :
                     action.type}
                  </span>
                </div>
              ))}

              {/* Action buttons */}
                <div className="action-buttons">
                {(bets?.[street] || 0) === 0 ? (
                    <>
                    {street !== 'preflop' && (
                      <button 
                        className="check"
                        onClick={() => onAction(player.id, { type: 'Check' }, street)}
                      >
                        Check
                      </button>
                    )}
                    <button 
                      className="bet"
                      onClick={() => {
                        setPendingAction({ playerId: player.id, type: 'Bet', street: street });
                        setShowBetInput(true);
                      }}
                    >
                        Bet
                      </button>
                    </>
                ) : (
                  <>
                    <button 
                      className="call"
                      onClick={() => onAction(player.id, { type: 'Call' }, street)}
                    >
                      Call ${bets?.[street] || 0}
                      </button>
                    <button 
                      className="raise"
                      onClick={() => {
                        setPendingAction({ playerId: player.id, type: 'Raise', street: street });
                        setShowBetInput(true);
                      }}
                    >
                        Raise
                      </button>
                    </>
                  )}
                <button 
                  className="fold"
                  onClick={() => onAction(player.id, { type: 'Fold' }, street)}
                >
                    Fold
                  </button>
                </div>
              </div>
          </div>
        ))}
      </div>

          {showBetInput && pendingAction && (
            <BetInput
              onConfirm={(amount) => {
                if (pendingAction) {
                  onAction(pendingAction.playerId, { 
                    type: pendingAction.type, 
                    amount 
                  }, pendingAction.street);
                  setShowBetInput(false);
                  setPendingAction(null);
                }
              }}
              onCancel={() => {
                setShowBetInput(false);
                setPendingAction(null);
              }}
              effectiveStack={currentBet}
              onConfirmAllIn={() => {
                onAction(pendingAction.playerId, { 
                  type: pendingAction.type, 
                  amount: currentBet, 
                  isAllIn: true 
                }, pendingAction.street);
                setShowBetInput(false);
                setPendingAction(null);
              }}
            />
      )}
    </div>
  );
};

// Add this new component above the BuddyPage class
interface HandHistoryImportProps {
  initiateAnalysis: (handId: string, handHistory: string, heroUsername: string) => void; // Add heroUsername
  analysis: any | null;
  isAnalyzing: boolean;
  error: string | null;
  heroUsername: string; // Add heroUsername prop
  onHeroUsernameChange: (event: React.ChangeEvent<HTMLInputElement>) => void; // Add handler prop
  customInstructions: string; // ADDED: Custom instructions value
  onCustomInstructionsChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void; // ADDED: Custom instructions handler
  // ADDED: Analysis status props
  analysisStatusLoading: boolean;
  analysisStatus: { isPremium: boolean; remainingAnalyses: number } | null;
  analysisStatusError: string | null;
  // --- ADDED Improvement Props ---
  improvementFeedback: string;
  isImprovingAnalysis: boolean;
  onImprovementFeedbackChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onImproveAnalysisRequest: (handId: string) => void;
  // --- END ADDED ---
  // --- ADDED Confirmation Props ---
  latestHandId: string | null;
  sessionId: string; 
  // --- END ADDED ---
  // <<< ADD Online Blind Props >>>
  onlineSmallBlind: number;
  onlineBigBlind: number;
  onOnlineSmallBlindChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOnlineBigBlindChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const HandHistoryImport: React.FC<HandHistoryImportProps> = ({ 
  initiateAnalysis, 
  analysis, 
  isAnalyzing, 
  error: analysisError, 
  heroUsername, // Destructure props
  onHeroUsernameChange, // Destructure props
  customInstructions, // ADDED: Destructure custom instructions
  onCustomInstructionsChange, // ADDED: Destructure custom instructions handler
  // ADDED: Destructure analysis status props
  analysisStatusLoading,
  analysisStatus,
  analysisStatusError,
  // --- Destructure Improvement Props ---
  improvementFeedback,
  isImprovingAnalysis,
  onImprovementFeedbackChange,
  onImproveAnalysisRequest,
  // --- END Destructure ---
  // --- ADDED Destructure Confirmation Props ---
  latestHandId,
  sessionId,
  // <<< Destructure Online Blind Props >>>
  onlineSmallBlind,
  onlineBigBlind,
  onOnlineSmallBlindChange,
  onOnlineBigBlindChange
}) => {
  const [handHistory, setHandHistory] = useState<string>('');
  const [showError, setShowError] = useState<string | null>(null); // Local error for import validation

  // Use useEffect to sync showError with analysisError from props
  useEffect(() => {
    setShowError(analysisError); // Show backend errors
  }, [analysisError]);

  const handleImport = () => {
    // Basic validation
    if (!handHistory.trim()) {
      setShowError('Please paste hand history text.');
      return;
    }
    // Add more specific validation if needed (e.g., regex for known formats)

    setShowError(null); // Clear local errors
    const handId = Math.random().toString(36).substring(2, 7).toUpperCase(); 
    console.log(`HandHistoryImport: Initiating analysis for Hand ID: ${handId}`);
    initiateAnalysis(handId, handHistory, heroUsername); 
  };

  // Helper function to get player ID safely
  const getPlayerIdSafe = () => {
    try {
      return PlayFab.getPlayerId();
    } catch (e) {
      console.error("Error getting PlayFab Player ID:", e);
      return null; // Return null or a placeholder if needed
    }
  }
  const playerId = getPlayerIdSafe(); // Get player ID once

  return (
    <div className="hand-history-import" style={{ padding: '1rem' }}> {/* Add padding to main container */}

      <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Import Hand History</h3> {/* Adjust heading margin */}
      
      {/* <<< MOVED Online Blind Inputs to TOP >>> */}
      <div className="online-blind-inputs" style={{ margin: '0 0 0.75rem 0', display: 'flex', gap: '1rem', alignItems: 'center' }}> {/* Adjusted Margins, added align-items */}
        <div className="blind-input" style={{ display: 'flex', alignItems: 'center' }}>
          <label htmlFor="onlineSmallBlindInput" style={{ marginRight: '5px' }}>SB:</label> {/* Simplified label */}
          <input 
            type="number"
            id="onlineSmallBlindInput"
            value={onlineSmallBlind}
            onChange={onOnlineSmallBlindChange}
            placeholder="e.g., 0.50"
            style={{ width: '70px' }} // Slightly reduced width
          />
        </div>
        <div className="blind-input" style={{ display: 'flex', alignItems: 'center' }}>
          <label htmlFor="onlineBigBlindInput" style={{ marginRight: '5px' }}>BB:</label> {/* Simplified label */}
          <input 
            type="number"
            id="onlineBigBlindInput"
            value={onlineBigBlind}
            onChange={onOnlineBigBlindChange}
            placeholder="e.g., 1.00"
            style={{ width: '70px' }} // Slightly reduced width
          />
        </div>
      </div>
      {/* --- END Moved Online Blind Inputs --- */}

      {/* Removed instruction <p> tag */}

      <textarea
        value={handHistory}
        onChange={(e) => setHandHistory(e.target.value)}
        placeholder="Paste hand history here..."
        rows={10} 
        className="hand-history-textarea"
        style={{ marginBottom: '0.5rem', width: '100%', boxSizing: 'border-box' }} // Reduced margin, ensure full width
      />
      
      {/* Username Input */}
      <div className="hero-username-input-container" style={{ margin: '0.5rem 0 0.5rem 0' }}> {/* Adjusted Margins */}
         <label htmlFor="heroUsername" style={{ display: 'block', marginBottom: '3px' }}>Your Username:</label> {/* Stack label */}
         <input 
           type="text" 
           id="heroUsername"
           value={heroUsername} 
           onChange={onHeroUsernameChange}
           placeholder="Enter your screen name"
           className="hero-username-input-field" 
           style={{ width: '100%', boxSizing: 'border-box' }} // Ensure full width
         />
      </div>

      {/* Custom Instructions */}
      <textarea
         id="customInstructions" 
         value={customInstructions}
         onChange={onCustomInstructionsChange}
         placeholder="Ask PokerBuddy a question!"
         rows={3} 
         className="custom-instructions-textarea"
         style={{ marginBottom: '0.5rem', width: '100%', boxSizing: 'border-box' }} // Reduced margin, ensure full width
       />

      {/* Import Button */}
      <button 
          onClick={handleImport} 
          disabled={isAnalyzing || isImprovingAnalysis} 
          className="import-button"
          style={{ marginTop: '0.25rem', width: '100%' }} // Adjusted Margin, make full width
      >
        {isAnalyzing ? 'Analyzing...' : 'Import & Analyze'}
      </button>

      {/* Status Area */}
      <div className="import-status-area" style={{ marginTop: '1rem' }}> 
        {/* Display local validation errors or backend errors FIRST */}
        {showError && <p className="error-message" style={{ color: 'red' }}>Error: {showError}</p>}

        {/* Display Analysis Status */}
        {analysisStatusLoading && <p>Loading analysis status...</p>}
        {analysisStatusError && <p className="error-message" style={{ color: 'red' }}>Error loading status: {analysisStatusError}</p>}
        {analysisStatus && !analysisStatus.isPremium && (
          <p>Free analyses remaining today: {analysisStatus.remainingAnalyses}</p>
        )}
        {analysisStatus && analysisStatus.isPremium && (
           <p style={{ color: 'gold' }}>Premium Account: Unlimited Analyses ✨</p>
        )}

        {/* Confirmation Block - show only if success (no error) */}
        {latestHandId && !analysisError && playerId && (
          <div className="confirmation-section" style={{ marginTop: '1rem' }}>
            <p className="confirmation-message" style={{ color: '#4ade80', fontWeight: 'bold' }}>
              Analysis requested for Hand <code>{latestHandId}</code>
              {sessionId && sessionId !== 'adhoc' && ` in session ${sessionId}`}
              {(!sessionId || sessionId === 'adhoc') && ` (Adhoc)`}.
            </p>
            <Link 
              to={`/history/user/${playerId}/sessions/${sessionId || 'adhoc'}`}
              className="action-button view-analysis-link" 
              style={{ marginTop: '0.5rem', display: 'inline-block' }}
            >
              View Session
            </Link>
          </div>
        )}
      </div> 
      {/* End Status Area */}

      {/* Improvement Section */}
      {/* ... improvement content ... */}

    </div> 
  );
};

// Main BuddyPage Component
interface BuddyPageState {
  loading: boolean;
  error: string | null;
  currentStreet: Street;
  selectedCard: string | null;
  showCardPicker: boolean;
  cardPickerTarget: keyof Hand | null;
  hand: Hand;
  players: Player[];
  currentPlayerId: number | null;
  pot: number;
  currentBet: number;
  setupComplete: boolean;
  numVillains: number;
  bets: Record<Street, number>;
  isAnalyzing: boolean;
  analysis: any | null;
  analysisError: string | null;
  showBetInput: boolean;
  pendingAction: {
    playerId: number;
    type: 'Bet' | 'Raise';
    street: Street;
  } | null;
  pendingActionStreet?: Street | null;
  showSummary: boolean;
  activeTab: 'replayer' | 'import' | 'voice' | 'sessionUpload'; // <<< ADDED 'sessionUpload'
  smallBlind: number;
  bigBlind: number;
  customInstructions: string;
  mobileVisibleStreet: Street;
  _lastActionWasDelete?: boolean; // Add flag to help componentDidUpdate
  analysisStatusLoading: boolean;
  analysisStatus: { isPremium: boolean; remainingAnalyses: number } | null;
  analysisStatusError: string | null;
  heroUsername: string; // Add heroUsername state
  // ADDED: Tournament state
  isTournament: boolean;
  tournamentDetails: {
    prizeStructure: string;
    levelStructure: string;
    playersRemaining: number | string; // Use string to allow empty input
    totalEntrants: number | string;    // Use string to allow empty input
    stage: string;
    payJumpInfo: string;
  };
  // --- ADDED State for Improvement Feature ---
  improvementFeedback: string;
  isImprovingAnalysis: boolean;
  lastSubmittedSummary: NewHandSummaryJson | string | null; // Store the original summary/history
  pollingForImprovementSince: string | null; // Timestamp of the analysis being improved
  // --- END ADDED State ---

  // --- ADDED Session/Hand Sharing State ---
  sessionId: string;
  lastSessionIdSuggestion: string | null; // <-- Restore state
  latestHandId: string | null;
  // --- END ADDED ---
  declaredWinnerIds: number[]; // <<< ADDED state for declared winners
  isChoppedPot: boolean; // <<< ADDED state for chopped pot
  onlineSmallBlind: number; // <<< ADDED for Online SB
  onlineBigBlind: number;   // <<< ADDED for Online BB

  // <<< ADDED Voice Input State >>>
  isRecording: boolean;
  audioBlob: Blob | null;
  recordingError: string | null;
  mediaRecorder: MediaRecorder | null;
  isUploadingAudio: boolean;
  uploadError: string | null;
  uploadProgress: number | null;
  latestAudioHandId: string | null; // Confirmation ID after triggering upload
  // <<< END Voice Input State >>>

  // <<< ADDED Session File Upload State >>>
  sessionFile: File | null;
  isUploadingSessionFile: boolean;
  sessionFileUploadError: string | null;
  sessionFileUploadProgress: number | null;
  latestSessionUploadId: string | null; // Confirmation ID after triggering session upload
  // <<< END Session File Upload State >>>
}

// Explicit type for the pending action state object
type PendingActionState = {
  playerId: number;
  type: 'Bet' | 'Raise';
  street: Street;
};

// Add these interfaces at the top with other interfaces
interface CloudScriptRequest {
  FunctionName: string;
  FunctionParameter: {
    [key: string]: any;
  };
}

interface CloudScriptResponse {
  success: boolean;
  data?: {
    FunctionResult: any;
  };
}

// Move these functions OUTSIDE and BEFORE the class definition
const canPlayerAct = (player: Player, players: Player[], street: Street, bets: Record<Street, number>) => {
  const orderedPlayers = getOrderedPlayers(players, street);
  
  // For first action in any street
  if (players.every(p => !p.actions[street] || p.actions[street].length === 0)) {
    // Get the first player in the ordered list for this street
    const firstToAct = orderedPlayers[0];
    // Only the first player in position order can act
    return player.id === firstToAct.id;
  }

  // After first action, everyone can act
  return true;
};

// --- NEW JSON Hand Summary Interfaces ---

// ADDED: Export HandSummaryMetadata
export interface HandSummaryMetadata {
  handId: string;
  timestamp: string; // ISO 8601 format
  handType: 'Live' | 'Online';
  tableType: string; // e.g., 'Cash Game', 'Tournament' - Hardcoded for now
  stakes: {
    smallBlind: number;
    bigBlind: number;
  };
}

// ADDED: Export HandSummaryPlayer
export interface HandSummaryPlayer {
  id: number;
  name: string;
  isHero: boolean;
  position: Position;
  initialStack: number;
}

// ADDED: Export HandSummaryHeroCards
export interface HandSummaryHeroCards {
  holeCard1: string;
  holeCard2: string;
}

// Updated Action interface for the new array structure
// ADDED: Export HandSummaryActionWithPlayerId
export interface HandSummaryActionWithPlayerId {
  playerId: number;
  type: ActionType; // 'Check' | 'Bet' | 'Call' | 'Raise' | 'Fold'
  amount?: number; // Amount is optional
  // actionNumber is removed
}

// ADDED: Export HandSummaryStreet
export interface HandSummaryStreet {
  potSizeAtStart: number;
  potSizeAtEnd: number;
  cardsDealt?: string[];
  // Changed actions to be an array of the new type
  actions: HandSummaryActionWithPlayerId[];
}

// --- ADDED: Export NewHandSummaryJson ---
export interface NewHandSummaryJson {
  metadata: HandSummaryMetadata;
  players: HandSummaryPlayer[];
  heroCards?: HandSummaryHeroCards;
  board?: string[];
  preflop: HandSummaryStreet;
  flop: HandSummaryStreet;
  turn: HandSummaryStreet;
  river: HandSummaryStreet;
  finalPot: number;
  // ADDED: Optional tournament details
  tournamentDetails?: TournamentDetails;
  declaredWinnerIds?: number[]; // <<< ADDED: Optional array of winner IDs
  isChoppedPot?: boolean; // <<< ADDED: Optional boolean for chopped pot
  heroTotalContribution?: number; // <<< ADDED: To pass calculated value
}

// --- END NEW JSON Hand Summary Interfaces ---

// --- ADD TournamentDetails Interface ---
interface TournamentDetails {
  prizeStructure: string;
  levelStructure: string;
  playersRemaining: number | string;
  totalEntrants: number | string;
  stage: string;
  payJumpInfo: string;
}
// --- END TournamentDetails Interface ---

// Helper: Find the first player to act preflop, skipping those who have already folded preflop
function getFirstToActPreflop(players: Player[]): number | null {
  const preflopOrder = ['UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
  const ordered = [...players].sort((a, b) => preflopOrder.indexOf(a.position) - preflopOrder.indexOf(b.position));
  // Debug log: print the sorted order and who is first to act
  console.log('Preflop order:', ordered.map(p => `${p.name} (${p.position})`));
  const first = ordered.find(p => !p.actions.preflop.some(a => a.type === 'Fold'));
  console.log('First to act:', first?.name, first?.position);
  return first ? first.id : null;
}

class BuddyPage extends React.Component<{}, BuddyPageState> {
  constructor(props: {}) {
    super(props);

    // Define initial BB first
    const initialBigBlind = 2;
    const initialSmallBlind = 1; // <<< Define initial SB

    const initialPlayers: Player[] = [
      {
        id: 0,
        name: 'Hero',
        isHero: true,
        position: 'UTG' as Position, // Default position, might be overridden
        stack: 100 * initialBigBlind, // Default stack 100bb
        actions: { preflop: [], flop: [], turn: [], river: [] }
      },
      {
        id: 1,
        name: 'Villain',
        isHero: false,
        position: 'BB' as Position, // Default position
        stack: 100 * initialBigBlind, // Default stack 100bb
        actions: { preflop: [], flop: [], turn: [], river: [] }
      }
    ];

    // --- REVISED Initial Player Logic --- 
    let initialPlayerId: number | null = null;
    if (initialPlayers.length === 2) {
      // Heads Up: SB acts first preflop
      const sb = initialPlayers.find(p => p.position === 'SB');
      initialPlayerId = sb ? sb.id : initialPlayers[0]?.id ?? null;
    } else {
      initialPlayerId = getFirstToActPreflop(initialPlayers);
    }
    // --- END REVISED Initial Player Logic ---

    this.state = {
      loading: false,
      error: null,
      currentStreet: 'preflop',
      selectedCard: null,
      showCardPicker: false,
      cardPickerTarget: null,
      hand: {
        holeCard1: '',
        holeCard2: '',
        flopCard1: '',
        flopCard2: '',
        flopCard3: '',
        turnCard: '',
        riverCard: ''
      },
      players: initialPlayers,
      currentPlayerId: initialPlayerId, // Initialize with corrected logic
      pot: 0,
      currentBet: 0,
      setupComplete: true, // Assuming setup is complete for now
      numVillains: initialPlayers.length - 1,
      bets: {
        preflop: 0, // Initialize preflop bet? Blinds might go here later.
        flop: 0,
        turn: 0,
        river: 0
      },
      isAnalyzing: false,
      analysis: null,
      analysisError: null,
      showBetInput: false,
      pendingAction: null,
      pendingActionStreet: null,
      showSummary: false,
      activeTab: 'replayer', // <<< DEFAULT TAB
      smallBlind: initialSmallBlind, // Use initial value
      bigBlind: initialBigBlind,   // Use initial value
      customInstructions: '',
      mobileVisibleStreet: 'preflop',
      // --- Initialize new state ---
      analysisStatusLoading: true,
      analysisStatus: null,
      analysisStatusError: null,
      heroUsername: '', // Initialize heroUsername
      // --- End Initialize new state ---
      // ADDED: Initialize tournament state
      isTournament: false,
      tournamentDetails: {
        prizeStructure: '',
        levelStructure: '',
        playersRemaining: '', // Init as empty string
        totalEntrants: '',    // Init as empty string
        stage: '',
        payJumpInfo: '',
      },
      // --- Initialize Improvement State ---
      improvementFeedback: '',
      isImprovingAnalysis: false,
      lastSubmittedSummary: null,
      pollingForImprovementSince: null, // Initialize timestamp state
      // --- End Initialize Improvement State ---

      // --- ADDED Session/Hand Sharing State Initialization ---
      sessionId: '', // Start with blank session ID
      lastSessionIdSuggestion: null, // <-- Restore state
      latestHandId: null,
      // --- END ADDED ---
      declaredWinnerIds: [], // <<< Initialize declaredWinnerIds as empty array
      isChoppedPot: false, // <<< Initialize isChoppedPot to false
      onlineSmallBlind: initialSmallBlind, // <<< Initialize Online SB
      onlineBigBlind: initialBigBlind,   // <<< Initialize Online BB

      // <<< Initialize Voice Input State >>>
      isRecording: false,
      audioBlob: null,
      recordingError: null,
      mediaRecorder: null,
      isUploadingAudio: false,
      uploadError: null,
      uploadProgress: null,
      latestAudioHandId: null,
      // <<< END Initialize Voice Input State >>>

      // <<< Initialize Session File Upload State >>>
      sessionFile: null,
      isUploadingSessionFile: false,
      sessionFileUploadError: null,
      sessionFileUploadProgress: null,
      latestSessionUploadId: null,
      // <<< END Initialize Session File Upload State >>>
    };
  }

  // --- ADD Handler for Tournament Detail Changes ---
  handleTournamentDetailChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    this.setState(prevState => ({
      tournamentDetails: {
        ...prevState.tournamentDetails,
        [name]: value,
      }
    }));
  };
  // --- END Handler ---

  // --- ADD Handler for Hero Username --- 
  handleHeroUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ heroUsername: event.target.value });
  };
  // --- END Handler --- 

  // --- ADD Handler for Custom Instructions ---
  handleCustomInstructionsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ customInstructions: event.target.value });
  };
  // --- END Handler ---

  // --- ADD Handler for Improvement Feedback Change ---
  handleImprovementFeedbackChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ improvementFeedback: event.target.value });
  };
  // --- END Handler ---

  // --- ADD Handler for Session ID Change ---
  handleSessionIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Remove all whitespace, #, and | characters
    const newSessionId = event.target.value.replace(/[\s#|]+/g, ''); 
    console.log('Session ID changed (invalid chars removed):', newSessionId);
    this.setState({ sessionId: newSessionId });
    // Save sanitized ID to localStorage
    try {
      if (newSessionId) {
        localStorage.setItem('pokerBuddySessionId', newSessionId);
        console.log('Saved Session ID to localStorage', newSessionId);
      } else {
        // Optionally remove if cleared, or keep the last one?
        // localStorage.removeItem('pokerBuddySessionId'); 
        // console.log('Removed Session ID from localStorage');
      }
    } catch (error) {
      console.error('Error saving sessionId to localStorage:', error);
    }
  };
  // --- END Handler ---

  // --- ADD Handler for Go To Hand --- 
  handleGoToHand = () => {
    console.log("handleGoToHand triggered");
    const { latestHandId, sessionId } = this.state;
    if (latestHandId && sessionId) {
      // TODO: Implement navigation logic
      // Example: window.location.href = `/session/${sessionId}/hand/${latestHandId}`;
      console.log(`Placeholder: Navigate to session ${sessionId}, hand ${latestHandId}`);
      // Replace with actual navigation using react-router or similar
      // this.props.history.push(`/session/${sessionId}/hand/${latestHandId}`); 
      alert(`Navigation to session ${sessionId}, hand ${latestHandId} not implemented yet.`);
    } else {
      console.warn("Go To Hand clicked but latestHandId or sessionId is missing.");
    }
  };
  // --- END Handler ---

  // --- ADD Handler for Share Hand --- 
  handleShareHand = () => {
    console.log("handleShareHand triggered");
    const { latestHandId, sessionId } = this.state;
    if (latestHandId && sessionId) {
      const shareUrl = `${window.location.origin}/shared?session=${sessionId}&hand=${latestHandId}`;
      // TODO: Implement a nicer sharing UI (e.g., copy to clipboard, modal)
      console.log(`Placeholder: Share URL: ${shareUrl}`);
      // Simple copy to clipboard attempt
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          alert(`Share link copied to clipboard!\n${shareUrl}`);
        })
        .catch(err => {
          console.error('Failed to copy share URL: ', err);
          alert(`Could not copy link. Share URL: ${shareUrl}`);
        });
    } else {
      console.warn("Share Hand clicked but latestHandId or sessionId is missing.");
    }
  };
  // --- END Handler ---

  // --- ADD Handler for Improve Analysis Request ---
  handleImproveAnalysisRequest = (handId: string) => {
    const { improvementFeedback, analysis, lastSubmittedSummary } = this.state;
  
    if (!analysis || !lastSubmittedSummary || !improvementFeedback.trim()) {
      console.error("Missing required data for improvement request:", { analysis, lastSubmittedSummary, improvementFeedback });
      this.setState({ analysisError: "Cannot improve analysis: Missing original data or feedback." });
      return;
    }
  
    // --- Store the timestamp of the analysis we are improving --- 
    const previousTimestamp = analysis.timestamp; 
    this.setState({ 
        isImprovingAnalysis: true, 
        // isAnalyzing: true, // Keep this false if we don't show loading popup
        analysisError: null,
        // pollingForImprovementSince: previousTimestamp // No longer needed
    }); // Use isAnalyzing for the popup?
  
    const functionParameter: any = {
        handId: handId,
        isImprovementRequest: true,
        // Ensure originalSummary is sent as a string
        originalSummary: typeof lastSubmittedSummary === 'string' ? lastSubmittedSummary : JSON.stringify(lastSubmittedSummary), 
        previousAnalysis: JSON.stringify(analysis), // Send the current analysis object as a string
        userFeedback: improvementFeedback,
        // We need the handType, get it from the existing analysis
        handType: analysis.handType || (typeof lastSubmittedSummary === 'object' && lastSubmittedSummary?.metadata?.handType) || 'Live', // Fallback logic for handType
        // Add heroUsername if it was part of the original request (check lastSubmittedSummary or analysis)
        ...(analysis.heroUsername && { heroUsername: analysis.heroUsername }), 
        // TODO: Consider if tournamentDetails need to be re-sent. Assuming backend uses originalSummary for this.
        sessionId: this.state.sessionId // --- ADDED sessionId ---
    };

    const request: CloudScriptRequest = { FunctionName: "analyzePokerHand", FunctionParameter: functionParameter };
    console.log(`Firing CloudScript analyzePokerHand for IMPROVEMENT on handId: ${handId}`);

    PlayFab.executeCloudScript(request)
        .then(result => {
            // Check if the overall API call failed
            if (!result.success) {
                console.error(`PlayFab API call to executeCloudScript failed during IMPROVEMENT for handId ${handId}.`, result);
                this.setState({ isAnalyzing: false, isImprovingAnalysis: false, analysisError: "Failed to send improvement request (API Error)." });
                return; 
            }

            // Check the CloudScript function's result for synchronous errors
            const outerFunctionResult = result.data?.FunctionResult;
            const innerFunctionResult = outerFunctionResult?.FunctionResult;

            if (innerFunctionResult && innerFunctionResult.success === false) {
                const errorMessage = innerFunctionResult.error || "Improvement request denied by server.";
                console.error(`CloudScript function analyzePokerHand returned an error during IMPROVEMENT for handId ${handId}:`, errorMessage);
                this.setState({ 
                    isAnalyzing: false, 
                    isImprovingAnalysis: false, 
                    analysisError: errorMessage, 
                    // Keep existing analysis displayed on error
                });
                return; 
            }

            // CloudScript submitted successfully, show confirmation
            console.log(`CloudScript IMPROVEMENT request submitted successfully for handId: ${handId}.`);
            this.setState({ 
                improvementFeedback: '', // Clear feedback 
                isImprovingAnalysis: false, // Turn off improving flag
                latestHandId: handId, // Set latestHandId to show buttons/confirmation
                analysis: null, // Clear old analysis to show confirmation
                analysisError: null, // Clear any previous error
                // Restore suggestion logic: Update last used suggestion
                lastSessionIdSuggestion: this.state.sessionId === "adhoc" ? null : this.state.sessionId 
            }); 
            // REMOVED Polling Call
            // this.startPollingForAnalysis(handId, previousTimestamp); 
        })
        .catch(error => {
            console.error(`Network/SDK Error submitting executeCloudScript for IMPROVEMENT on handId ${handId}:`, error);
            // Reset the improvement timestamp state on error too
            this.setState({ isAnalyzing: false, isImprovingAnalysis: false, analysisError: "Failed to send improvement request (Network Error)." });
            // REMOVED Polling Clear
            // if (this.pollInterval) clearInterval(this.pollInterval);
            // this.pollInterval = null;
        });
  };
  // --- END Handler ---

  componentDidMount() {
    // Check login status when component mounts
    if (!Account.isLoggedIn()) {
      window.dispatchEvent(new CustomEvent('showLogin', {
        detail: {
          callback: () => {
            // Force a re-render after login
            this.forceUpdate();
            this.fetchAnalysisStatus(); // Fetch status after login
          }
        }
      }));
    } else {
      this.fetchAnalysisStatus(); // Fetch status if already logged in
    }

    // --- BEGIN Load last session ID suggestion ---
    try {
      const savedSessionId = localStorage.getItem('pokerBuddySessionId');
      console.log('BuddyPage componentDidMount: Found saved Session ID in localStorage:', savedSessionId);
      if (savedSessionId) {
        this.setState({ lastSessionIdSuggestion: savedSessionId });
      }
    } catch (error) {
      console.error('Error reading pokerBuddySessionId from localStorage:', error);
    }
    // --- END Load last session ID suggestion ---

    // --- BEGIN Load saved values --- 
    try {
      const savedSessionId = localStorage.getItem('pokerBuddySessionId');
      // console.log('BuddyPage componentDidMount: Found saved Session ID in localStorage:', savedSessionId);
      if (savedSessionId) {
        this.setState({ lastSessionIdSuggestion: savedSessionId });
      }

      // Load online blinds <<< ADDED
      const savedOnlineSB = localStorage.getItem('pokerBuddyOnlineSB');
      const savedOnlineBB = localStorage.getItem('pokerBuddyOnlineBB');
      const initialStateSB = this.state.onlineSmallBlind; // Get initial default
      const initialStateBB = this.state.onlineBigBlind; // Get initial default
      
      const updateState: Partial<BuddyPageState> = {}; // Use partial type for state updates
      if (savedOnlineSB !== null) {
          updateState.onlineSmallBlind = Number(savedOnlineSB) || initialStateSB;
      }
      if (savedOnlineBB !== null) {
          updateState.onlineBigBlind = Number(savedOnlineBB) || initialStateBB;
      }
      // Apply state updates if any were found
      if (Object.keys(updateState).length > 0) {
         this.setState(updateState as BuddyPageState);
      }
      // console.log('Loaded online blinds from localStorage:', updateState);

    } catch (error) {
      console.error('Error reading values from localStorage:', error);
    }
    // --- END Load saved values ---
    // ADDED: Fetch analysis status here if needed after login or on mount
    if (Account.isLoggedIn()) {
        this.fetchAnalysisStatus(); 
    }
  }

  componentWillUnmount() {
    // REMOVED Interval Clearing Logic
  }

  // --- NEW Method to fetch analysis status --- // REMOVED Polling Clear
  fetchAnalysisStatus = async () => {
    console.log("Fetching analysis status...");
    this.setState({ analysisStatusLoading: true, analysisStatusError: null });
    try {
      const response = await PlayFab.executeCloudScript('getPlayerAnalysisStatus');
      
      // --- Improved Error Handling ---
      if (!response.success) {
        // Overall API call failed
        console.error("PlayFab API call to executeCloudScript failed:", response);
        this.setState({
          analysisStatusError: 'Failed to contact server for status.',
          analysisStatusLoading: false,
        });
        return; // Stop further processing
      }

      // API call succeeded, check the FunctionResult
      const outerFunctionResult = response.data?.FunctionResult;
      const innerFunctionResult = outerFunctionResult?.FunctionResult; // Access the nested FunctionResult
      
      if (innerFunctionResult?.success) {
        // CloudScript function succeeded
        console.log("Analysis status fetched:", innerFunctionResult);
        this.setState({
          analysisStatus: {
            isPremium: innerFunctionResult.isPremium,
            remainingAnalyses: innerFunctionResult.remainingAnalyses,
          },
          analysisStatusLoading: false,
        });
      } else {
        // CloudScript function reported failure or inner result structure was unexpected
        const errorMessage = innerFunctionResult?.error || 'Unknown error from CloudScript'; // Corrected: use innerFunctionResult
        console.error("CloudScript function getPlayerAnalysisStatus failed:", errorMessage, innerFunctionResult?.details); // Corrected: use innerFunctionResult
        this.setState({
          analysisStatusError: errorMessage,
          analysisStatusLoading: false,
        });
      }
      // --- End Improved Error Handling ---

    } catch (error) {
      console.error("Error calling getPlayerAnalysisStatus CloudScript:", error);
      this.setState({
        analysisStatusError: 'Network error fetching status',
        analysisStatusLoading: false,
      });
    }
  };
  // --- END NEW Method ---

  handleCardSelect = (card: string) => {
    if (!this.state.cardPickerTarget) return;

    const target = this.state.cardPickerTarget;
    this.setState(prevState => ({
      hand: {
        ...prevState.hand,
        [target]: card
      },
      showCardPicker: false,
      cardPickerTarget: null
    }));
  };

  handleAction = (playerId: number, action: ExtendedAction, actionStreet: Street) => {
    if (action.type !== 'Delete' && playerId !== this.state.currentPlayerId) {
        console.warn(`Action attempted by player ${playerId}, but current player is ${this.state.currentPlayerId}`);
        return; 
    }

    if (action.type === 'Delete') {
        // --- Refactored Delete Action Logic v7 (Pass current card state) --- 
        const deletedActionStreet = actionStreet; 
        const actionIndexToDelete = action.actionIndex;

        if (actionIndexToDelete === undefined || actionIndexToDelete < 0) {
            console.error("Delete action called without a valid actionIndex.");
            this.setState({ _lastActionWasDelete: false }); // Clear flag on error
            return; 
        }

        // Use a single setState call, passing the determined card street
        this.setState((prevState) => {
            // --- Determine latest card street INSIDE updater, using this.state (v11) ---
            // --- REMOVED Card/Action Street Calculation (v12) ---
            /*
            let currentLatestCardStreet: Street = 'preflop';
            // READ this.state DIRECTLY HERE
            if (typeof this.state.hand.riverCard === 'string' && this.state.hand.riverCard.length === 2) {
                currentLatestCardStreet = 'river';
            } else if (typeof this.state.hand.turnCard === 'string' && this.state.hand.turnCard.length === 2) {
                currentLatestCardStreet = 'turn';
            } else if (typeof this.state.hand.flopCard1 === 'string' && this.state.hand.flopCard1.length === 2) {
                currentLatestCardStreet = 'flop';
            }
            console.log(`[handleAction/Delete Update v11] Read currentLatestCardStreet from this.state: ${currentLatestCardStreet}`);
            */
            // --- END Determine latest card street ---

            // Create the new player state *after* deletion
            let playersAfterDelete = prevState.players.map(player => {
                if (player.id === playerId) {
                    const updatedActions = { ...player.actions };
                    if (updatedActions[deletedActionStreet] && actionIndexToDelete < updatedActions[deletedActionStreet].length) {
                        updatedActions[deletedActionStreet] = updatedActions[deletedActionStreet].filter((_, index) => index !== actionIndexToDelete);
                        // Ensure empty array if last action deleted
                        if (updatedActions[deletedActionStreet].length === 0) { 
                            delete updatedActions[deletedActionStreet]; 
                        }
                        return { ...player, actions: updatedActions };
                    }
                }
                return player;
            });

            // --- REVISED BACKTRACKING LOGIC v13 (Recalculate Bets relative to deleted street) --- 
            let effectiveBets = { ...prevState.bets }; 
            const deletedStreetIdx = STREET_ORDER.indexOf(deletedActionStreet); 

            // Recalculate the highest bet ON the DELETED street based on remaining actions
            let highestBetOnDeletedStreet = 0;
            playersAfterDelete.forEach(player => {
                let finalPlayerContribution = 0;
                (player.actions[deletedActionStreet] || []).forEach(action => {
                     if (action.type === 'Bet' || action.type === 'Raise' || action.type === 'Call') {
                         finalPlayerContribution = action.amount || 0;
                     }
                 });
                 highestBetOnDeletedStreet = Math.max(highestBetOnDeletedStreet, finalPlayerContribution);
            });
            effectiveBets[deletedActionStreet] = highestBetOnDeletedStreet;
            
             // Clear bets for streets AFTER the deleted one
            for (let i = deletedStreetIdx + 1; i < STREET_ORDER.length; i++) { 
                effectiveBets[STREET_ORDER[i]] = 0; 
            }
            
            console.log(`[handleAction/Delete Update v13] Recalculated Bets relative to ${deletedActionStreet}:`, effectiveBets);
            // --- END REVISED BACKTRACKING LOGIC v13 ---

            // Set state for delete - Keep currentStreet for now, let componentDidUpdate determine the effective street.
            return {
                ...prevState,
                players: playersAfterDelete,
                bets: effectiveBets,
                currentStreet: prevState.currentStreet, // Keep for now
                mobileVisibleStreet: prevState.mobileVisibleStreet, // Keep for now
                currentPlayerId: null, // Set null temporarily
                _lastActionWasDelete: true // Set flag
            };
        });
        // --- End Delete Logic ---
    } else {
        // --- Non-Delete Action Logic (Wrapped in setState) --- 
        this.setState(prevState => {
            let finalAction = { ...action };
            const player = prevState.players.find(p => p.id === playerId);

            // --- Calculate Effective Stack (v15) ---
            let effectiveStack = player?.stack ?? 0; // Start with initial stack (or 0 if undefined)
            if (effectiveStack > 0) { // Only subtract if initial stack is known
                STREET_ORDER.forEach(s => {
                    if (STREET_ORDER.indexOf(s) < STREET_ORDER.indexOf(actionStreet)) { // Only previous streets
                        (player?.actions[s] || []).forEach(a => {
                            if (a.type === 'Bet' || a.type === 'Raise' || a.type === 'Call') {
                                effectiveStack -= (a.amount || 0);
                            }
                        });
                    }
                });
            }
            effectiveStack = Math.max(0, effectiveStack); // Ensure stack doesn't go below 0
            console.log(`[HandleAction v15] Calculated effective stack for P${playerId} on ${actionStreet}: ${effectiveStack}`);
            // --- End Calculate Effective Stack ---

            // --- Handle All-in Logic (v15) ---
            let isGoingAllIn = action.isAllIn === true; // Check if triggered by All-in button
            let actionAmount = action.amount;

            if (isGoingAllIn) {
                // Bet/Raise All-in: Amount is the entire effective stack
                actionAmount = effectiveStack;
                finalAction.amount = actionAmount; // Update finalAction for bet calculation
                console.log(`[HandleAction v15] P${playerId} ALL-IN (${action.type}) for ${actionAmount}`);
            }

            if (finalAction.type === 'Call') {
                const currentBet = prevState.bets[actionStreet]; 
                const callAmount = currentBet; // Call amount is the current bet
                finalAction.amount = callAmount; // Set amount for the call
                // Check if Call results in All-in
                if (effectiveStack <= callAmount) {
                    actionAmount = effectiveStack; // They can only call their remaining stack
                    finalAction.amount = actionAmount; // Adjust the action amount
                    isGoingAllIn = true; // Mark as all-in
                    console.log(`[HandleAction v15] P${playerId} Calls ALL-IN for ${actionAmount} (Bet was ${callAmount})`);
                } else {
                    actionAmount = callAmount;
                }
            }
            // --- End Handle All-in Logic ---
            
            const actionToAdd: BaseAction = { 
                type: finalAction.type as ActionType, 
                // Use the potentially adjusted actionAmount
                ...(actionAmount !== undefined && { amount: actionAmount }), 
                // Add the isAllIn flag if applicable
                ...(isGoingAllIn && { isAllIn: true }) 
            };
            
            if (!(['Check', 'Bet', 'Call', 'Raise', 'Fold'] as ActionType[]).includes(actionToAdd.type)) {
                console.error("Error: Invalid action type:", actionToAdd.type);
                return { ...prevState, _lastActionWasDelete: false }; 
            }
            
            const newPlayers = prevState.players.map(p => {
                if (p.id === playerId) {
                    const existingActions = p.actions[actionStreet] || []; 
                    return { ...p, actions: { ...p.actions, [actionStreet]: [...existingActions, actionToAdd] } };
                }
                return p;
            });
            
            let newBets = { ...prevState.bets };
            // Update bets based on the final action amount (could be all-in amount)
            if ((finalAction.type === 'Bet' || finalAction.type === 'Raise') && finalAction.amount != null) {
                newBets[actionStreet] = finalAction.amount;
                // Add logging for bet update (v17)
                console.log(`[HandleAction v17] Updating bets for ${actionStreet} to ${finalAction.amount} due to ${finalAction.type} (All-in: ${isGoingAllIn})`);
            } else {
                 console.log(`[HandleAction v17] Not updating bets for ${actionStreet} (Action: ${finalAction.type}, Amount: ${finalAction.amount})`);
            }

            // Pass new state into calculation
            let nextPlayerId = this.calculateNextPlayerToAct(newPlayers, actionStreet, newBets, playerId);
            let nextStreet = actionStreet;
            let nextMobileStreet = prevState.mobileVisibleStreet;
            let finalBets = newBets;
            
            if (nextPlayerId === null && actionStreet !== 'river') {
                 console.log(`End of ${actionStreet} detected. Proceeding to next street.`);
                 const currentStreetIndex = STREET_ORDER.indexOf(actionStreet);
                 nextStreet = STREET_ORDER[currentStreetIndex + 1];
                 nextMobileStreet = nextStreet;
                 finalBets = { ...newBets, [nextStreet]: 0 }; 
                 nextPlayerId = this.calculateNextPlayerToAct(newPlayers, nextStreet, finalBets, null); 
                 console.log(`First player to act on ${nextStreet}: ${nextPlayerId}`);
            } else if (nextPlayerId === null && actionStreet === 'river') {
                 console.log("End of river detected. Hand betting complete.");
            }

            return {
                ...prevState,
                players: newPlayers,
                bets: finalBets, 
                currentPlayerId: nextPlayerId, 
                currentStreet: nextStreet, 
                mobileVisibleStreet: nextMobileStreet, 
                _lastActionWasDelete: false 
            };
        });
        // --- End Non-Delete Logic ---
    }
};

  handleCardClick = (cardKey: keyof Hand, value?: string) => {
    if (value) {
      // Direct value provided from text input
      this.setState(prevState => ({
        hand: {
          ...prevState.hand,
          [cardKey]: value
        }
      }));
    } else {
      // Show card picker for manual selection
      this.setState({
        showCardPicker: true,
        cardPickerTarget: cardKey
      });
    }
  };

  handlePositionChange = (playerId: number, newPosition: Position) => {
    this.setState(prevState => {
      // Update the player's position first
      const updatedPlayers = prevState.players.map(player => {
        if (player.id === playerId) {
          return { ...player, position: newPosition };
        }
        return player;
      });

      // --- Recalculate currentPlayerId ONLY if no actions have been taken anywhere ---
      let nextCurrentPlayerId = prevState.currentPlayerId; // Default to previous
      const handHasStarted = prevState.players.some(p => 
        STREET_ORDER.some(s => (p.actions[s] || []).length > 0)
      );

      if (!handHasStarted) {
        console.log("[handlePositionChange] Hand not started, recalculating initial player ID...");
        nextCurrentPlayerId = getFirstToActPreflop(updatedPlayers);
      }
      // --- End recalculation ---

      return {
        players: updatedPlayers,
        currentPlayerId: nextCurrentPlayerId // Set the potentially updated player ID
      };
    });
  };

  addVillain = () => {
    this.setState(prevState => {
      const availablePositions = this.getAvailablePositions(prevState.players, -1);
      if (availablePositions.length === 0) return prevState;

      // Get current Big Blind value from state
      const currentBigBlind = prevState.bigBlind;

      // Find the next unique id (max id + 1)
      const nextId = prevState.players.length === 0
        ? 1
        : Math.max(...prevState.players.map(p => p.id)) + 1;

      // Find the next available villain number for the name
      const villainNumbers = prevState.players
        .filter(p => !p.isHero && p.name.startsWith('Villain '))
        .map(p => parseInt(p.name.replace('Villain ', ''), 10))
        .filter(n => !isNaN(n));
      let nextVillainNumber = 1;
      while (villainNumbers.includes(nextVillainNumber)) {
        nextVillainNumber++;
      }

      return {
        ...prevState,
        players: [
          ...prevState.players,
          {
            id: nextId,
            name: `Villain ${nextVillainNumber}`,
            isHero: false,
            position: availablePositions[0],
            stack: 100 * currentBigBlind, // Set default stack to 100bb
            actions: { preflop: [], flop: [], turn: [], river: [] }
          }
        ],
        numVillains: prevState.numVillains + 1
      };
    });
  };

  removeVillain = (villainId: number) => {
    if (villainId === 0) return; // Can't remove Hero
    this.setState(prevState => {
      const newPlayers = prevState.players.filter(p => p.id !== villainId);
      // Find the last player who acted on the current street
      const street = prevState.currentStreet;
      let lastPlayerId: number | null = null;
      // Build action sequence in position order, round by round
      const orderedPlayers = getOrderedPlayers(newPlayers, street);
      const maxRounds = Math.max(0, ...orderedPlayers.map(p => (p.actions[street] || []).length));
      for (let round = 0; round < maxRounds; round++) {
        for (const player of orderedPlayers) {
          const action = player.actions[street]?.[round];
          if (action) {
            lastPlayerId = player.id;
          }
        }
      }
      // Recalculate next player to act
      const nextPlayerId = this.calculateNextPlayerToAct(
        newPlayers,
        street,
        prevState.bets,
        lastPlayerId
      );
      return {
        ...prevState,
        players: newPlayers,
        numVillains: prevState.numVillains - 1,
        currentPlayerId: nextPlayerId
      };
    });
  };

  getNextAvailablePosition = (heroPosition: Position): Position => {
    const index = POSITION_ORDER.indexOf(heroPosition);
    return POSITION_ORDER[(index + 1) % POSITION_ORDER.length];
  };

  renderSetup() {
    return (
      <div className="setup-phase">
        <h2>Select Hero's Position</h2>
        <div className="position-buttons">
          {POSITION_ORDER.map(pos => (
            <button
              key={pos}
              onClick={() => this.handlePositionChange(0, pos)}
              className="position-button"
            >
              {pos}
            </button>
          ))}
        </div>
      </div>
    );
  }

  getAvailablePositions = (players: Player[], currentPlayerId: number): Position[] => {
    const occupiedPositions = players
      .filter(p => p.id !== currentPlayerId)
      .map(p => p.position);
    return POSITION_ORDER.filter(pos => !occupiedPositions.includes(pos));
  };

  // --- REFACTORED generateHandSummary ---
  generateHandSummary = (handId: string): NewHandSummaryJson => {
    const { 
      hand, 
      players, 
      smallBlind, 
      bigBlind, 
      activeTab, 
      isTournament, 
      tournamentDetails, 
      declaredWinnerIds, 
      isChoppedPot // <<< Destructure new state
    } = this.state; 
    const streets: Street[] = ['preflop', 'flop', 'turn', 'river'];
    const timestamp = new Date().toISOString();
    const handType = activeTab === 'replayer' ? 'Live' : 'Online';
    // UPDATED: Determine tableType based on isTournament state
    const tableType = isTournament ? 'Tournament' : 'Cash Game'; 
    const metadata: HandSummaryMetadata = {
      handId: handId, 
      timestamp: timestamp, 
      handType: handType, 
      tableType: tableType, // Use determined tableType
      stakes: { smallBlind, bigBlind },
    };
    const summaryPlayers: HandSummaryPlayer[] = players.map(p => ({
      id: p.id, name: p.name, isHero: p.isHero, position: p.position, initialStack: p.stack
    }));
    const heroCardsData = (hand.holeCard1 && hand.holeCard2) ? { holeCard1: hand.holeCard1, holeCard2: hand.holeCard2 } : undefined;
    const boardCards = [ hand.flopCard1, hand.flopCard2, hand.flopCard3, hand.turnCard, hand.riverCard ].filter(card => card && card.length === 2);
    const board = boardCards.length > 0 ? boardCards : undefined;
    
    // *** Remove internal pot calculation logic from summary generation ***
    // const streetDetails: { [S in Street]: HandSummaryStreet } = { ... };
    // let currentPot = smallBlind + bigBlind;
    // streetDetails.preflop.potSizeAtStart = currentPot;
    // let lastActionStreetIndex = -1;
    // ... loop calculating potSizeAtEnd ...
    // let finalPotInternal: number;
    // if (lastActionStreetIndex === -1) { finalPotInternal = streetDetails.preflop.potSizeAtStart; }
    // else { finalPotInternal = streetDetails[streets[lastActionStreetIndex]].potSizeAtEnd; }

    // *** ADDED: Simplified street details generation (without pot calculation) ***
    const streetDetails: { [S in Street]?: Omit<HandSummaryStreet, 'potSizeAtStart' | 'potSizeAtEnd'> } = {}; 
    for (const street of streets) {
        const streetActionsArray: HandSummaryActionWithPlayerId[] = [];
        const orderedPlayers = getOrderedPlayers(players, street);
        const maxActionsThisStreet = Math.max(0, ...orderedPlayers.map(p => (p.actions[street] || []).length));
        let streetHadActions = false;
        for (let round = 0; round < maxActionsThisStreet; round++) {
             orderedPlayers.forEach(player => {
                 const action = player.actions[street]?.[round];
                 if (action) {
                     streetHadActions = true;
                     const jsonAction: HandSummaryActionWithPlayerId = {
                         playerId: player.id,
                         type: action.type,
                         ...(action.amount !== undefined && { amount: action.amount }),
                         ...((action as BaseAction & { isAllIn?: boolean }).isAllIn === true && { isAllIn: true }) 
                     };
                     streetActionsArray.push(jsonAction);
                 }
             });
        }
        
        // Only include street details if actions occurred or cards were dealt
        let cardsDealt: string[] | undefined = undefined;
        if (street === 'flop' && hand.flopCard1 && hand.flopCard2 && hand.flopCard3) { cardsDealt = [hand.flopCard1, hand.flopCard2, hand.flopCard3]; }
        else if (street === 'turn' && hand.turnCard) { cardsDealt = [hand.turnCard]; }
        else if (street === 'river' && hand.riverCard) { cardsDealt = [hand.riverCard]; }

        if (streetActionsArray.length > 0 || cardsDealt) {
            streetDetails[street] = {
                actions: streetActionsArray,
                ...(cardsDealt && { cardsDealt: cardsDealt })
            };
        }
    }
    // *** END Simplified street details generation ***

    // <<< Calculate Hero Contribution >>>
    const heroContribution = this.calculateHeroContribution();
    // <<< Get Final Pot from the corrected function >>>
    const finalPot = this.calculateRunningTotal('river');

    // Initialize with all REQUIRED fields, using default pot sizes for streets
    const summaryJson: NewHandSummaryJson = {
      metadata: metadata,
      players: summaryPlayers,
      preflop: { 
          actions: streetDetails.preflop?.actions || [], 
          cardsDealt: streetDetails.preflop?.cardsDealt,
          potSizeAtStart: 0, // Add default
          potSizeAtEnd: 0    // Add default
      },
      flop: { 
          actions: streetDetails.flop?.actions || [], 
          cardsDealt: streetDetails.flop?.cardsDealt,
          potSizeAtStart: 0, // Add default
          potSizeAtEnd: 0    // Add default
      },
      turn: { 
          actions: streetDetails.turn?.actions || [], 
          cardsDealt: streetDetails.turn?.cardsDealt,
          potSizeAtStart: 0, // Add default
          potSizeAtEnd: 0    // Add default
      },
      river: { 
          actions: streetDetails.river?.actions || [], 
          cardsDealt: streetDetails.river?.cardsDealt,
          potSizeAtStart: 0, // Add default
          potSizeAtEnd: 0    // Add default
      },
      finalPot: finalPot, // Use the pot calculated by calculateRunningTotal
      // Conditionally add OPTIONAL fields
      ...(heroCardsData && { heroCards: heroCardsData }),
      ...(board && { board: board }),
      ...(isTournament && { tournamentDetails: tournamentDetails }),
      ...(declaredWinnerIds && declaredWinnerIds.length > 0 && { declaredWinnerIds: declaredWinnerIds }),
      ...(isChoppedPot && { isChoppedPot: isChoppedPot }),
      ...(heroContribution !== undefined && { heroTotalContribution: heroContribution })
    };

    // No checks or cast needed now
    return summaryJson;
  };
  // --- END REFACTORED generateHandSummary ---

  // --- REVISED Pot Calculation Logic ---
  calculateRunningTotal = (targetStreet: Street): number => {
    const streets: Street[] = ['preflop', 'flop', 'turn', 'river'];
    let totalPot = 0;

    // Initialize pot ONLY with blinds.
    totalPot = this.state.smallBlind + this.state.bigBlind;

    // Process actions street by street up to the target street
    for (let i = 0; i <= streets.indexOf(targetStreet); i++) {
        const currentStreet = streets[i];
        let contributionsThisStreet: Record<number, number> = {}; // Track contributions ONLY for the current street
        this.state.players.forEach(p => contributionsThisStreet[p.id] = 0);
        let betLevelToCallThisStreet = 0; // Track the highest contribution level reached ON this street

        // Add blind contributions for preflop tracking
        if (currentStreet === 'preflop') {
            const sbPlayer = this.state.players.find(p => p.position === 'SB');
            const bbPlayer = this.state.players.find(p => p.position === 'BB');
            if (sbPlayer) {
                // Initialize contribution *for this street* with the blind
                contributionsThisStreet[sbPlayer.id] = this.state.smallBlind;
            }
            if (bbPlayer) {
                // Ensure BB cumulative amount isn't less than BB if also SB
                contributionsThisStreet[bbPlayer.id] = Math.max(contributionsThisStreet[bbPlayer.id] || 0, this.state.bigBlind);
            }
             // Initial level to call preflop is the BB amount
             betLevelToCallThisStreet = this.state.bigBlind;
        }


        const orderedPlayers = getOrderedPlayers(this.state.players, currentStreet);
        const maxActionsThisStreet = Math.max(0, ...orderedPlayers.map(p => (p.actions[currentStreet] || []).length));

        // Process actions chronologically round by round for THIS street
        for (let round = 0; round < maxActionsThisStreet; round++) {
             orderedPlayers.forEach(player => {
                 const action = player.actions[currentStreet]?.[round];

                 if (action) {
                    const playerContributionSoFarThisStreet = contributionsThisStreet[player.id] || 0;

                     if (action.type === 'Bet' || action.type === 'Raise') {
                         // The action amount represents the NEW total level player wants to reach for THIS STREET
                         const targetLevelThisStreet = action.amount || 0;
                         // Amount actually added is the difference from what they already put in THIS STREET
                         const amountToAdd = Math.max(0, targetLevelThisStreet - playerContributionSoFarThisStreet);

                         if (amountToAdd > 0) {
                             totalPot += amountToAdd;
                             contributionsThisStreet[player.id] += amountToAdd;
                             // The level to call is now the total contributed BY THIS PLAYER on this street
                             betLevelToCallThisStreet = contributionsThisStreet[player.id];
                         }
                     } else if (action.type === 'Call') {
                         // Amount to add is the difference between the level to call and what they've put in THIS STREET
                         const amountToAdd = Math.max(0, betLevelToCallThisStreet - playerContributionSoFarThisStreet);

                         if (amountToAdd > 0) {
                            totalPot += amountToAdd;
                            contributionsThisStreet[player.id] += amountToAdd;
                         }
                     }
                 }
             });
        }
        // Reset street-specific tracking for the next street iteration implicitly
    }
    return totalPot;
};
  // --- END REVISED Pot Calculation Logic ---

  // --- ADDED: Calculate Hero's Total Contribution ---
  calculateHeroContribution = (): number => {
    const streets: Street[] = ['preflop', 'flop', 'turn', 'river'];
    let heroCumulativeContribution = 0; // Variable to track ONLY Hero's contribution
    const heroPlayer = this.state.players.find(p => p.isHero);

    if (!heroPlayer) return 0;

    // Initialize Hero's contribution based on blinds
    if (heroPlayer.position === 'SB') {
        heroCumulativeContribution = this.state.smallBlind;
    }
    if (heroPlayer.position === 'BB') {
        // Use Math.max in case Hero was also SB (e.g., heads-up)
        heroCumulativeContribution = Math.max(heroCumulativeContribution, this.state.bigBlind);
    }

    // Process actions street by street
    for (const currentStreet of streets) {
        let contributionsThisStreet: Record<number, number> = {}; // Track contributions ONLY for the current street for ALL players
        this.state.players.forEach(p => contributionsThisStreet[p.id] = 0);
        let betLevelToCallThisStreet = 0; // Track the highest contribution level reached ON this street by anyone

        // Add blind contributions to the street tracker for ALL players (needed to calculate call amounts correctly)
        if (currentStreet === 'preflop') {
            const sbPlayer = this.state.players.find(p => p.position === 'SB');
            const bbPlayer = this.state.players.find(p => p.position === 'BB');
            if (sbPlayer) {
                contributionsThisStreet[sbPlayer.id] = this.state.smallBlind;
            }
            if (bbPlayer) {
                contributionsThisStreet[bbPlayer.id] = Math.max(contributionsThisStreet[bbPlayer.id] || 0, this.state.bigBlind);
            }
             betLevelToCallThisStreet = this.state.bigBlind; // Initial level for calls preflop
        }

        const orderedPlayers = getOrderedPlayers(this.state.players, currentStreet);
        const maxActionsThisStreet = Math.max(0, ...orderedPlayers.map(p => (p.actions[currentStreet] || []).length));

        // Process actions chronologically round by round for THIS street
        for (let round = 0; round < maxActionsThisStreet; round++) {
             orderedPlayers.forEach(player => {
                 const action = player.actions[currentStreet]?.[round];

                 if (action) {
                    const playerContributionSoFarThisStreet = contributionsThisStreet[player.id] || 0;

                     if (action.type === 'Bet' || action.type === 'Raise') {
                         // Action amount is the target level for THIS street
                         const targetLevelThisStreet = action.amount || 0;
                         // New money added by this player this street
                         const amountToAdd = Math.max(0, targetLevelThisStreet - playerContributionSoFarThisStreet);

                         if (amountToAdd > 0) {
                             // Update this player's contribution *for this street*
                             contributionsThisStreet[player.id] += amountToAdd;
                             // Update the level required for others to call *on this street*
                             betLevelToCallThisStreet = contributionsThisStreet[player.id];
                             // If this player IS the Hero, add the new money to their cumulative total
                             if (player.id === heroPlayer.id) {
                                 heroCumulativeContribution += amountToAdd;
                             }
                         }
                     } else if (action.type === 'Call') {
                         // New money needed to call the current street level
                         const amountToAdd = Math.max(0, betLevelToCallThisStreet - playerContributionSoFarThisStreet);

                         if (amountToAdd > 0) {
                            // Update this player's contribution *for this street*
                            contributionsThisStreet[player.id] += amountToAdd;
                             // If this player IS the Hero, add the new money to their cumulative total
                             if (player.id === heroPlayer.id) {
                                 heroCumulativeContribution += amountToAdd;
                             }
                         }
                     }
                 }
             });
        }
    }
    return heroCumulativeContribution;
  };
  // --- END Calculate Hero's Total Contribution ---

  // --- ADD BACK initiateAnalysis and analyzeHand ---
  initiateAnalysis = (handId: string, summaryOrHistory: string | NewHandSummaryJson, heroUsername?: string) => { // Add optional heroUsername
    console.log("Initiating analysis request...");
    const isOnlineImport = typeof summaryOrHistory === 'string'; // Check if it's an online import
    // Reset analysis state before new request
    this.setState({ 
      isAnalyzing: true, 
      analysis: null, 
      analysisError: null, 
      isImprovingAnalysis: false, // Ensure improvement flag is off for initial analysis
      pollingForImprovementSince: null // Clear polling timestamp
    });

    // Determine the Session ID to use: Use state value, or "adhoc" if blank/whitespace
    const sessionIdToSend = this.state.sessionId.trim() || "adhoc";
    console.log(`Using Session ID for analysis: ${sessionIdToSend}`);

    // Determine if summary is object or string
    let summaryPayload: string;
    let parsedSummaryForDb: NewHandSummaryJson | null = null;
    if (typeof summaryOrHistory === 'object' && summaryOrHistory !== null) {
      summaryPayload = JSON.stringify(summaryOrHistory);
      parsedSummaryForDb = summaryOrHistory; // Keep object for DB write step
    } else {
      summaryPayload = summaryOrHistory; 
      // Attempt to parse if it's a string (for Online hands potentially)
      try {
        parsedSummaryForDb = JSON.parse(summaryOrHistory);
      } catch (e) {
        console.warn("Could not parse summary string for DB write step.");
      }
    }

    // Store the summary exactly as it was submitted for potential improvement later
    this.setState({ lastSubmittedSummary: summaryOrHistory }); 

    // <<< DETERMINE Correct Blinds to Send >>>
    const smallBlindToSend = isOnlineImport ? this.state.onlineSmallBlind : this.state.smallBlind;
    const bigBlindToSend = isOnlineImport ? this.state.onlineBigBlind : this.state.bigBlind;
    console.log(`Using Blinds for request: SB=${smallBlindToSend}, BB=${bigBlindToSend} (Source: ${isOnlineImport ? 'Online' : 'Live'})`);

    const functionParams: any = {
      handId: handId,
      summary: summaryPayload,
      handType: isOnlineImport ? 'Online' : (this.state.isTournament ? 'Tournament' : 'Live'), 
      customInstructions: this.state.customInstructions,
      heroUsername: heroUsername || this.state.heroUsername || null,
      sessionId: sessionIdToSend, 
      shouldAwardXP: true,
      // <<< ADD Consistent Blind Parameters >>>
      smallBlind: smallBlindToSend,
      bigBlind: bigBlindToSend
    };

    // Add tournament details if it's a tournament hand and type wasn't 'Online'
    if (functionParams.handType === 'Tournament') {
      functionParams.tournamentDetails = this.state.tournamentDetails;
    }

    // Remove the old conditional logic for online blinds
    // if (isOnlineImport) { ... }

    // <<< ADD THIS LOG >>>
    console.log('Sending to CloudScript:', functionParams);

    PlayFab.executeCloudScript('analyzePokerHand', functionParams)
      .then((response) => {
        console.log('CloudScript analyzePokerHand response:', response);
        // Access nested result
        const nestedResult = response.data?.FunctionResult?.FunctionResult;

        if (response.success && nestedResult?.success) {
          console.log(`Analysis successfully triggered for hand ${nestedResult.handId}. Client will poll.`);
          // Start polling for this handId (Add polling logic here or call existing)
          // Assuming polling logic exists elsewhere and uses nestedResult.handId
          this.setState({ 
              isAnalyzing: false, 
              analysisError: null, 
              latestHandId: nestedResult.handId, 
              // Restore suggestion logic: Update last used suggestion
              lastSessionIdSuggestion: sessionIdToSend === "adhoc" ? null : sessionIdToSend 
          });
        } else {
          // Handle error from CloudScript function itself
          const errorMessage = nestedResult?.error || response.data?.FunctionResult?.error || 'Unknown error triggering analysis';
          console.error('Error triggering analysis:', errorMessage, nestedResult?.details);
          this.setState({ 
              analysisError: `Error triggering analysis: ${errorMessage}`,
              isAnalyzing: false,
              isImprovingAnalysis: false // Ensure this is also reset on error
          });
        }
      })
      .catch(error => {
        console.error('Error calling analyzePokerHand CloudScript:', error);
        this.setState({ 
          analysisError: `Failed to trigger analysis: ${error.message}`,
          isAnalyzing: false,
          isImprovingAnalysis: false // Ensure this is also reset on error
        });
      });
  };

  analyzeHand = () => {
    const handId = Math.random().toString(36).substring(2, 7).toUpperCase();
    const summaryJson = this.generateHandSummary(handId);
    this.initiateAnalysis(handId, summaryJson);
  };
  // --- END ADD BACK initiateAnalysis and analyzeHand ---

  // --- ADD goToNextStreet Method ---
  goToNextStreet = () => {
    this.setState(prevState => {
      const currentStreetIndex = STREET_ORDER.indexOf(prevState.currentStreet);
      if (currentStreetIndex >= STREET_ORDER.length - 1) {
        return null; // Already on river or invalid state
      }
      const nextStreet = STREET_ORDER[currentStreetIndex + 1];

      // Reset bet amount for the next street
      const newBets = { ...prevState.bets, [nextStreet]: 0 };

      return {
        currentStreet: nextStreet,
        mobileVisibleStreet: nextStreet, // Keep mobile view in sync
        bets: newBets,
        // Optionally reset currentPlayer or other street-specific state here
      };
    });
  };
  // --- END goToNextStreet Method ---

  // --- ADD Placeholder for calculateNextPlayerToAct ---
  calculateNextPlayerToAct = (players: Player[], street: Street, bets: Record<Street, number>, lastActedPlayerId: number | null): number | null => {
    // --- Start of Street Handling --- 
    if (lastActedPlayerId === null) {
         const activePlayersAtStart = players.filter(p => 
             !STREET_ORDER.slice(0, STREET_ORDER.indexOf(street))
                 .some(s => (p.actions[s] || []).some(a => a.type === 'Fold'))
        );
        const orderedPlayersAtStart = getOrderedPlayers(activePlayersAtStart, street);
        console.log(`[calculateNextPlayer/Start] Street: ${street}, Active players:`, activePlayersAtStart.map(p => p.id)); // ADD LOG
        console.log(`[calculateNextPlayer/Start] Ordered players:`, orderedPlayersAtStart.map(p => p.id)); // ADD LOG
        if (orderedPlayersAtStart.length > 0) {
             const firstPlayerId = orderedPlayersAtStart[0].id;
             console.log(`[calculateNextPlayer/Start] Returning first player: ${firstPlayerId}`); // ADD LOG
             return firstPlayerId;
        } else {
            console.log(`[calculateNextPlayer/Start] No active players found? Returning null.`); // ADD LOG
            return null;
        }
    }
    // --- End Start of Street Handling ---

    console.log(`[CalculateNextPlayer v7] Calculating for ${street} after P${lastActedPlayerId} acted.`);

    // --- Filter out folded players ONLY initially (v18) ---
    const playersInHand = players.filter(p => { 
        const hasFolded = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
            .some(s => (p.actions[s] || []).some(a => a.type === 'Fold'));
        return !hasFolded;
    });

    // --- REMOVE Premature All-in Filter & Check ---
    /*
    const playersInPot = players.filter(p => { 
        // Check if folded on current or previous street
        const hasFolded = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
            .some(s => (p.actions[s] || []).some(a => a.type === 'Fold'));
        if (hasFolded) return false;
        
        // Check if player is all-in on current or previous street
        const isAllIn = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
            .some(s => (p.actions[s] || []).some(a => 
                // Cast action type here (v15.1 fix)
                (a as BaseAction & { isAllIn?: boolean }).isAllIn === true
            ));
        if (isAllIn) {
            console.log(`[CalculateNextPlayer v7] Player ${p.id} is all-in, filtering out.`);
            return false; // Exclude all-in players from taking further betting actions
        }
        
        return true; // Player is active and not all-in
    });

    if (playersInPot.length <= 1) {
        console.log(`[CalculateNextPlayer v7] 0 or 1 players left in pot who can act. Round over.`);
        return null; // Betting is over if 0 or 1 players can still act
    }
    */
    // --- End Filter --- 
    
    const orderedPlayers = getOrderedPlayers(playersInHand, street); // Use players still in the hand
    if (orderedPlayers.length === 0) {
        console.log(`[CalculateNextPlayer v8] No players left in hand. Round over.`);
        return null; 
    }
    // --- End Initial Filtering ---
    
    // Note: numActivePlayers now refers to players not folded, potentially including all-in players
    const numActivePlayers = orderedPlayers.length; 

    // --- Recalculate Street State v11 (Chronological Sequence Build) ---
    let currentMaxContribution = 0;
    let aggressorIdOnStreet: number | null = null;
    const streetContributions: Record<number, number> = {};
    const lastActionIndexMap: Record<number, number> = {};
    let lastAggressionSequenceIndex = -1;
    let bettingOpened = false;
    const streetActionSequence: {playerId: number, action: BaseAction, seqIndex: number}[] = [];
    let globalActionCounter = 0;
    let firstPlayerToActionThisStreetId: number | null = null;

    console.log(`[Recalc v11 - START] Street: ${street}`);
    const allPlayersOrderedOriginal = getOrderedPlayers(players, street); // Order based on position for the street

    // Determine the maximum number of actions any single player took on this street
    let maxActionCountPerPlayer = 0;
    players.forEach(p => { // Iterate through original players array
        maxActionCountPerPlayer = Math.max(maxActionCountPerPlayer, (p.actions[street] || []).length);
    });

    // Build the sequence chronologically
    for (let round = 0; round < maxActionCountPerPlayer; round++) {
         // CRITICAL: Use the positionally ordered list for the inner loop
         allPlayersOrderedOriginal.forEach(player => {
             const action = player.actions[street]?.[round]; // Get the action for this player at this round index
             if (action) {
                 // Record the first player who acted *among those still in the hand*
                 if (firstPlayerToActionThisStreetId === null && playersInHand.some(pih => pih.id === player.id)) {
                     firstPlayerToActionThisStreetId = player.id;
                 }
                 const actionIndexInSequence = globalActionCounter++;
                 streetActionSequence.push({playerId: player.id, action: action, seqIndex: actionIndexInSequence});
             }
         });
    }
    console.log(`[Recalc v11] Built action sequence with ${streetActionSequence.length} actions.`);
    // Log the built sequence for debugging
    console.log(`[Recalc v11] Sequence:`, streetActionSequence.map(a => `P${a.playerId} ${a.action.type}`));


    // Now, iterate through the chronologically built sequence to calculate contributions
    let previousMaxContributionBeforeAction = 0;
    streetActionSequence.forEach(({ playerId, action, seqIndex }) => { // Iterate the sequence
         console.log(`[Recalc v11] Processing Seq #${seqIndex}, Player ${playerId}, Action: ${action.type} ${action.amount ?? ''}`);
         let playerCurrentTotalContribution = streetContributions[playerId] || 0;

         if (action.type === 'Bet' || action.type === 'Raise') {
             playerCurrentTotalContribution = action.amount || 0;
             streetContributions[playerId] = playerCurrentTotalContribution;
             currentMaxContribution = playerCurrentTotalContribution;
             aggressorIdOnStreet = playerId;
             lastAggressionSequenceIndex = seqIndex; // Use sequence index
             bettingOpened = true;
             console.log(`  -> Contrib SET to: ${playerCurrentTotalContribution}, New Max: ${currentMaxContribution}`);
         } else if (action.type === 'Call') {
             // Call matches the contribution level required *before* this call
             playerCurrentTotalContribution = previousMaxContributionBeforeAction;
             streetContributions[playerId] = playerCurrentTotalContribution;
             console.log(`  -> Contrib SET to: ${playerCurrentTotalContribution} (Called level ${previousMaxContributionBeforeAction})`);
             // currentMaxContribution doesn't change on call
         } else if (action.type === 'Check' || action.type === 'Fold') {
             console.log(`  -> Contrib remains: ${playerCurrentTotalContribution}`);
             // Contribution doesn't change
         }

         lastActionIndexMap[playerId] = seqIndex; // Track last action index for the player

         // Update the max contribution seen *before* the next action in sequence
         previousMaxContributionBeforeAction = currentMaxContribution;
    });

    console.log(`[Recalc v11 - END] Street: ${street}, Final MaxContribution: ${currentMaxContribution}, Final Aggressor: ${aggressorIdOnStreet}, Final BettingOpened: ${bettingOpened}, First Eligible Actor: ${firstPlayerToActionThisStreetId}`);
    console.log("[Recalc v11 - END] Final Contributions: ", streetContributions);
    // --- End Recalculate Street State v11 ---

    // --- Check End of Round Conditions (using orderedPlayers which are playersInHand) ---
    let roundOver = false;
    const everyoneEligibleActed = orderedPlayers.every(p => lastActionIndexMap[p.id] !== undefined);

    // Use numActivePlayers (count of playersInHand)
    if (streetActionSequence.length >= numActivePlayers) {
        if (!bettingOpened) {
            // Check-Around End Logic (v9 - Use first *eligible* actor)
            if (everyoneEligibleActed && firstPlayerToActionThisStreetId !== null) {
                 const firstActorIndex = orderedPlayers.findIndex(p => p.id === firstPlayerToActionThisStreetId);
                 if (firstActorIndex !== -1) {
                     // Find player physically before the first actor in position order
                     const playerBeforeFirstActorIndex = (firstActorIndex - 1 + numActivePlayers) % numActivePlayers;
                     const playerBeforeFirstActorId = orderedPlayers[playerBeforeFirstActorIndex].id;
                     // Check if the very last action in the whole sequence was by this player
                     const lastActionInSequence = streetActionSequence[streetActionSequence.length - 1];
                     if (lastActionInSequence && lastActionInSequence.playerId === playerBeforeFirstActorId) {
                         console.log(`[CheckEnd v9] Round over: Last actor ${playerBeforeFirstActorId} is before first eligible actor ${firstPlayerToActionThisStreetId}`);
                         roundOver = true;
                     } else {
                        console.log(`[CheckEnd v9] Round cont: Last actor ${lastActionInSequence?.playerId} is NOT before first eligible actor ${firstPlayerToActionThisStreetId}`);
                     }
                 } else {
                    console.warn(`[CheckEnd v9] Could not find first eligible actor (${firstPlayerToActionThisStreetId}) in ordered players.`);
                 }
             } else {
                 console.log(`[CheckEnd v9] Round cont: Not everyone eligible acted or no first eligible actor recorded.`);
             }
        } else { // Betting IS open (use orderedPlayers for checking contributions)
            const allActivePlayersMatchedOrFoldedOrAllIn = orderedPlayers.every(p => {
                const isPlayerAllIn = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
                   .some(s => (p.actions[s] || []).some(a => (a as BaseAction & { isAllIn?: boolean }).isAllIn === true));
                // Use recalculated streetContributions
                return (streetContributions[p.id] || 0) === currentMaxContribution || isPlayerAllIn;
            });

            const lastAction = streetActionSequence.length > 0 ? streetActionSequence[streetActionSequence.length - 1] : null;
            const lastActualActionWasAggression = lastAction && (lastAction.action.type === 'Bet' || lastAction.action.type === 'Raise');

            let actionsCompletedCircle = false;
            if (aggressorIdOnStreet !== null) {
                 const aggressorIndex = orderedPlayers.findIndex(p => p.id === aggressorIdOnStreet);
                 if (aggressorIndex !== -1) {
                    const playerBeforeAggressorIndex = (aggressorIndex - 1 + numActivePlayers) % numActivePlayers;
                    const playerBeforeAggressorId = orderedPlayers[playerBeforeAggressorIndex].id;
                    // Check if the last action in the sequence was by the player before the aggressor
                    if (lastAction && lastAction.playerId === playerBeforeAggressorId) {
                        actionsCompletedCircle = true;
                    }
                 }
                 // HU Postflop closing the action check
                 if (numActivePlayers === 2 && street !== 'preflop' && lastAction && lastAction.playerId !== aggressorIdOnStreet && !lastActualActionWasAggression) {
                     actionsCompletedCircle = true;
                 }
            } else if (bettingOpened && !aggressorIdOnStreet) {
                // Handle case where initial bet is called around and closed by BB preflop?
                // This might need specific logic if BB checks their option.
                console.warn("[BetEnd v9] Betting opened but no aggressor tracked?");
            }


            if (allActivePlayersMatchedOrFoldedOrAllIn && actionsCompletedCircle && !lastActualActionWasAggression) {
                 console.log(`[BetEnd v9] Round over: Contributions matched/all-in, circle complete, last action not aggression.`);
                roundOver = true;
            } else {
                 console.log(`[BetEnd v9] Round cont: Matched/AI=${allActivePlayersMatchedOrFoldedOrAllIn}, Circle Done=${actionsCompletedCircle}, Last Aggro=${lastActualActionWasAggression}`);
            }
        }
    }
    
    // --- REFINED Alternative End Check v9 --- 
    if (!roundOver && bettingOpened && currentMaxContribution > 0) {
        // Find players who are still in the hand, not yet all-in, and haven't matched the current bet
        const playersWhoNeedToCallOrFold = playersInHand.filter(p => {
            const isPlayerAlreadyAllIn = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
                .some(s => (p.actions[s] || []).some(a => (a as BaseAction & { isAllIn?: boolean }).isAllIn === true));
            const playerContribution = streetContributions[p.id] || 0;
            
            // Keep players who are NOT all-in AND have contributed LESS than the max
            return !isPlayerAlreadyAllIn && playerContribution < currentMaxContribution;
        });

        // If there are NO players left who need to call or fold to the current bet, the round is over.
        // This covers cases where everyone facing the bet has either called, folded, or gone all-in themselves.
        if (playersWhoNeedToCallOrFold.length === 0 && streetActionSequence.length > 0) {
            console.log(`---> Round Detected Over on ${street} (Refined v9: No players left needing to call/fold to bet ${currentMaxContribution}) <---`);
            return null; 
        } else {
            console.log(`[EndCheck v9] Round cont: ${playersWhoNeedToCallOrFold.length} players need to call/fold.`);
        }
    }
    // --- End REFINED Alternative End Check --- 

    // --- Fallback Round Over Check (Only 1 player left in hand total) ---
    if (playersInHand.length <= 1 && streetActionSequence.length > 0) { 
        console.log(`---> Round Detected Over on ${street} (Fallback: Only ${playersInHand.length} player(s) left in hand total) <---`);
        return null;
    }

    if (roundOver) { // This is the flag set by the standard checks (check-around or normal betting close)
        console.log(`---> Round Detected Over on ${street} (Standard End) <---`);
        return null;
    }
    // --- End Check End of Round Conditions ---

    // --- Find Next Player To Act (from orderedPlayers = playersInHand) --- 
    const lastPlayerIndex = orderedPlayers.findIndex(p => p.id === lastActedPlayerId);
    const effectiveLastIndex = lastPlayerIndex === -1 ? numActivePlayers - 1 : lastPlayerIndex;
    const searchStartIndex = (effectiveLastIndex + 1) % numActivePlayers;

    for (let i = 0; i < numActivePlayers; i++) {
        const potentialNextPlayerIndex = (searchStartIndex + i) % numActivePlayers;
        const potentialPlayer = orderedPlayers[potentialNextPlayerIndex];
        
        // Check if this player is already all-in (v18)
        const isPlayerAlreadyAllIn = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
             .some(s => (potentialPlayer.actions[s] || []).some(a => (a as BaseAction & { isAllIn?: boolean }).isAllIn === true));
             
        if (isPlayerAlreadyAllIn) {
            console.log(`[FindNext v8] Skipping P${potentialPlayer.id} as they are already all-in.`);
            continue; // Skip this player, check the next one
        }

        // If player is not all-in, apply normal logic
        if (!bettingOpened) { 
             if (street === 'preflop' && potentialPlayer.position === 'BB' && lastActionIndexMap[potentialPlayer.id] === undefined) {
                  return potentialPlayer.id;
             }
             return potentialPlayer.id; 
        } else {
            const playerContribution = streetContributions[potentialPlayer.id] || 0;
            if (playerContribution < currentMaxContribution) { 
                return potentialPlayer.id;
            }
        }
    }
    // --- End Find Next Player To Act ---

    console.warn("Could not determine next player to act (end of loop). Returning null (assuming round over).");
    return null;
  };
  // --- END Placeholder --- 

  // --- REFINED clearHand Method ---
  clearHand = () => {
    console.log("Clearing hand state (including improvement AND voice state)..."); // Added voice state mention
    // REMOVED Polling Clear
    // if (this.pollInterval) {
    //   clearInterval(this.pollInterval);
    //   this.pollInterval = null; 
    //   console.log("Polling interval cleared by clearHand.");
    // }
  
      // Reset analysis and improvement state first
      this.setState({
          isAnalyzing: false,
          analysis: null,
          analysisError: null,
          showSummary: false, 
          customInstructions: '', 
          showBetInput: false,
          pendingAction: null,
          pendingActionStreet: null,
          // --- Reset Improvement State ---
          improvementFeedback: '',
          isImprovingAnalysis: false,
          lastSubmittedSummary: null,
          pollingForImprovementSince: null, // Reset timestamp state here too
          // --- End Reset ---
          declaredWinnerIds: [], // <<< Reset declared winners
          isChoppedPot: false, // <<< Reset chopped pot state
          // --- Reset Voice State ---
          isRecording: false,
          audioBlob: null,
          recordingError: null,
          mediaRecorder: null, // Ensure recorder instance is cleared if reset happens mid-recording
          isUploadingAudio: false,
          uploadError: null,
          uploadProgress: null,
          latestAudioHandId: null,
          // --- End Voice Reset ---
      }, () => {
        // Reset the rest in the callback
        this.setState(prevState => {
          const initialBigBlind = prevState.bigBlind;

          const resetPlayers = prevState.players.map(player => ({
            ...player,
            stack: 100 * initialBigBlind,
            actions: { preflop: [], flop: [], turn: [], river: [] }
          }));

          // Recalculate initial player ID based on reset player positions
          let initialPlayerId = getFirstToActPreflop(resetPlayers);
           // Heads Up: SB acts first preflop (check both SB/BB permutations)
           const sbPlayer = resetPlayers.find(p => p.position === 'SB');
           if (resetPlayers.length === 2 && sbPlayer) {
               initialPlayerId = sbPlayer.id;
           } else if (resetPlayers.length === 2 && !sbPlayer) {
              // If no SB, assume the first player is BB and second is SB? Or default? Let's stick to getFirstToActPreflop default for non-standard HU
               console.warn("[clearHand] Heads up but no player found in SB position during reset.");
           } else {
              initialPlayerId = getFirstToActPreflop(resetPlayers); // Default for >2 players
           }


          console.log("[clearHand] Reset players:", resetPlayers);
          console.log("[clearHand] Calculated initialPlayerId:", initialPlayerId);


          return {
            currentStreet: 'preflop',
            mobileVisibleStreet: 'preflop',
            selectedCard: null,
            showCardPicker: false,
            cardPickerTarget: null,
            hand: {
              holeCard1: '', holeCard2: '', flopCard1: '', flopCard2: '', flopCard3: '', turnCard: '', riverCard: ''
            },
            players: resetPlayers,
            currentPlayerId: initialPlayerId,
            pot: 0,
            currentBet: 0,
            bets: { preflop: 0, flop: 0, turn: 0, river: 0 },
            _lastActionWasDelete: false,
            tournamentDetails: {
              prizeStructure: '', levelStructure: '', playersRemaining: '', totalEntrants: '', stage: '', payJumpInfo: ''
            },
            // Analysis state already reset in the first setState call
          };
        }, () => {
           // REMOVED analysisResetKey update
           console.log("Hand state fully cleared.");
        });
      });
    };
  // --- END REFINED clearHand Method ---

  // --- ADD Handler for Declaring Winner ---
  handleDeclareWinner = (playerId: number) => {
    this.setState(prevState => {
      const currentWinners = prevState.declaredWinnerIds;
      const isAlreadyWinner = currentWinners.includes(playerId);
      let newWinners: number[];

      if (isAlreadyWinner) {
        // Remove the player ID if already present
        newWinners = currentWinners.filter(id => id !== playerId);
      } else {
        // Add the player ID if not present
        newWinners = [...currentWinners, playerId];
      }
      console.log("Declared winners updated:", newWinners); // Log for debugging
      return { declaredWinnerIds: newWinners };
    });
  };
  // --- END Handler ---

  // --- ADD Handler for Toggling Chop Pot ---
  handleToggleChop = () => {
    this.setState(prevState => {
      const newChopState = !prevState.isChoppedPot;
      console.log("Chopped pot toggled:", newChopState); // Log for debugging
      return { isChoppedPot: newChopState };
    });
  };
  // --- END Handler ---

  // --- ADD Handlers for Online Blinds --- 
  handleOnlineSmallBlindChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value) || 0;
    this.setState({ onlineSmallBlind: value });
    try {
      localStorage.setItem('pokerBuddyOnlineSB', value.toString()); // Save to localStorage
    } catch (error) {
      console.error('Error saving online SB to localStorage:', error);
    }
  };

  handleOnlineBigBlindChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value) || 0;
    this.setState({ onlineBigBlind: value });
    try {
      localStorage.setItem('pokerBuddyOnlineBB', value.toString()); // Save to localStorage
    } catch (error) {
      console.error('Error saving online BB to localStorage:', error);
    }
  };
  // --- END Handlers --- 

  render() {
    if (!Account.isLoggedIn()) {
      return null; // Return null while waiting for login
    }

    // Define streets array locally for reuse
    const streets: Street[] = ['preflop', 'flop', 'turn', 'river'];
    const nextStreetIndex = STREET_ORDER.indexOf(this.state.currentStreet) + 1;
    const nextStreetName = nextStreetIndex < STREET_ORDER.length ? STREET_ORDER[nextStreetIndex] : null;

    // --- Check if analysis should be disabled based on status ---
    const { analysisStatus, analysisStatusLoading, isAnalyzing, isImprovingAnalysis } = this.state;
    const shouldDisableAnalysisFlag = isAnalyzing || isImprovingAnalysis || 
        (
            !analysisStatusLoading && 
            analysisStatus && 
            !analysisStatus.isPremium && 
            analysisStatus.remainingAnalyses === 0
        );
    // Explicitly coerce to boolean to satisfy the 'disabled' prop type
    const finalDisabledState = !!shouldDisableAnalysisFlag; 
    // --- End Check ---
    
    console.log("Current analysis state:", this.state.analysis); // ADDED for debugging

    return (
      <div className="page">
        {/* REMOVED AnalysisLoadingPopup */}
        
        <div className="buddy-container">
          {/* --- Session ID Input Section (MOVED UP, shared state) --- */}
          {/* Display Session ID section only if NOT on the import tab */}
          {this.state.activeTab !== 'import' && (
            <div className="session-id-input-section" style={{ margin: '1rem 0', padding: '0 1rem' }}>
              <label htmlFor="sessionIdInput" style={{ marginRight: '10px', fontWeight: 'bold' }}>Session ID:</label>
              <input
                type="text"
                id="sessionIdInput"
                value={this.state.sessionId} // Shared state
                onChange={this.handleSessionIdChange}
                placeholder="Enter Session ID (Optional)"
                style={{ padding: '5px', marginRight: '10px', minWidth: '200px' }}
              />
              <p className="session-id-explanation" style={{ fontSize: '0.8em', color: '#a0aec0', marginTop: '4px' }}>
                Group related hands (e.g., "Sunday Grind"). Leave blank for individual analysis.
              </p>
              {this.state.lastSessionIdSuggestion && (
                <p className="session-id-suggestion" style={{ fontSize: '0.9em', marginTop: '5px', color: '#cbd5e1' }}>
                  <i>Last used:</i> <code style={{ backgroundColor: '#4a5568', padding: '2px 4px', borderRadius: '3px', cursor: 'pointer'}} onClick={() => this.setState({ sessionId: this.state.lastSessionIdSuggestion || '' })}>{this.state.lastSessionIdSuggestion}</code>
                </p>
              )}
            </div>
          )}
          {/* --- END Session ID Input Section --- */}

          <div className="tab-container">
            <div className="tab-buttons">
              <button
                className={`tab-button ${this.state.activeTab === 'replayer' ? 'active' : ''}`}
                onClick={() => this.setState({ activeTab: 'replayer' })}
              >
                Live Poker
              </button>
              <button
                className={`tab-button ${this.state.activeTab === 'import' ? 'active' : ''}`}
                onClick={() => this.setState({ activeTab: 'import' })}
              >
                Online Poker
              </button>
              {/* --- NEW Voice Input Tab Button --- */}
              <button
                className={`tab-button ${this.state.activeTab === 'voice' ? 'active' : ''}`}
                onClick={() => this.setState({ activeTab: 'voice' })}
              >
                Voice Input
              </button>
              {/* --- NEW Session Upload Tab Button --- */}
              <button
                className={`tab-button ${this.state.activeTab === 'sessionUpload' ? 'active' : ''}`}
                onClick={() => this.setState({ activeTab: 'sessionUpload' })}
              >
                Session Upload
              </button>
              {/* --- END NEW Button --- */}
            </div>

            {this.state.activeTab === 'replayer' ? (
              !this.state.setupComplete ? (
                this.renderSetup()
              ) : (
                <> { /* Start Fragment */}
                  <div className="hand-replayer">
                    {this.state.showCardPicker && ( 
                      <CardPicker 
                        show={this.state.showCardPicker}
                        onSelect={this.handleCardSelect}
                        onClose={() => this.setState({ showCardPicker: false })}
                        hand={this.state.hand}
                      />
                    )}

                    <Board 
                      hand={this.state.hand} 
                      onCardClick={this.handleCardClick}
                    />
                    
                    <div className="streets-container">
                      <div className="player-management">
                        <div className="player-controls">
                          <button onClick={this.addVillain} className="add-villain-btn">
                            Add Villain
                          </button>
                          {this.state.players.map(player => (
                            <div key={player.id} className="player-row">
                              <div 
                                className={`player-label ${window.innerWidth <= 768 ? 'player-label-vertical' : ''}`}
                                data-player-info={`${player.name} (${player.position})`}
                              >
                                <div>{player.name} ({player.position})</div>
                              </div>
                              <input
                                type="number"
                                value={player.stack === -1 ? '' : player.stack}
                                onChange={(e) => {
                                  const newValue = e.target.value === '' ? -1 : Number(e.target.value);
                                  this.setState(prevState => ({
                                    players: prevState.players.map(p => 
                                      p.id === player.id 
                                        ? { ...p, stack: newValue }
                                        : p
                                    )
                                  }));
                                }}
                                className="stack-input"
                                placeholder="Stack size"
                              />
                              <select 
                                value={player.position}
                                onChange={(e) => this.handlePositionChange(player.id, e.target.value as Position)}
                                className="position-select"
                              >
                                <option key={player.position} value={player.position}>
                                  {player.position}
                                </option>
                                {this.getAvailablePositions(this.state.players, player.id)
                                  .sort((a, b) => POSITION_ORDER.indexOf(a) - POSITION_ORDER.indexOf(b))
                                  .map(pos => (
                                    <option key={pos} value={pos}>{pos}</option>
                                  ))
                                }
                              </select>
                              {!player.isHero && (
                                <button 
                                  onClick={() => this.removeVillain(player.id)}
                                  className="remove-villain"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="blind-inputs">
                          <div className="blind-input">
                            <label>SB:</label>
                            <input 
                              type="number"
                              value={this.state.smallBlind}
                              onChange={(e) => this.setState({ smallBlind: Number(e.target.value) })}
                              placeholder="1"
                            />
                          </div>
                          <div className="blind-input">
                            <label>BB:</label>
                            <input 
                              type="number"
                              value={this.state.bigBlind}
                              onChange={(e) => this.setState({ bigBlind: Number(e.target.value) })}
                              placeholder="2"
                            />
                          </div>
                        </div>

                        {/* --- MOVED Tournament Toggle --- */}
                        <div className="tournament-toggle-section" style={{ marginTop: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                          <label htmlFor="tournament-toggle" style={{ marginRight: '10px', fontWeight: 'bold' }}>Tournament Mode:</label>
                          <input
                            type="checkbox"
                            id="tournament-toggle"
                            checked={this.state.isTournament}
                            onChange={(e) => this.setState({ isTournament: e.target.checked })}
                            style={{ height: '20px', width: '20px' }} // Corrected: Added missing quote
                          />
                        </div>
                        {/* --- END Tournament Toggle --- */}
  
                        {/* --- MOVED Conditional Tournament Details Form --- */}
                        {this.state.isTournament && (
                          <div className="tournament-details-form" style={{ marginTop: '1rem', border: '1px solid #444', padding: '1rem', borderRadius: '8px' }}>
                            <h4 style={{ marginTop: '0', marginBottom: '1rem' }}>Tournament Details</h4>
                            <div className="form-grid"> {/* Using a grid for layout */}
                              {/* Left Column */}
                              <div className="form-column">
                                <div className="form-group">
                                  <label htmlFor="totalEntrants">Total Entrants:</label>
                                  <input 
                                    type="number" 
                                    id="totalEntrants"
                                    name="totalEntrants"
                                    value={this.state.tournamentDetails.totalEntrants}
                                    onChange={this.handleTournamentDetailChange} 
                                    placeholder="e.g., 150"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="playersRemaining">Players Remaining:</label>
                                  <input 
                                    type="number" 
                                    id="playersRemaining"
                                    name="playersRemaining"
                                    value={this.state.tournamentDetails.playersRemaining}
                                    onChange={this.handleTournamentDetailChange} 
                                    placeholder="e.g., 27"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="stage">Tournament Stage:</label>
                                  <input 
                                    type="text" 
                                    id="stage"
                                    name="stage"
                                    value={this.state.tournamentDetails.stage}
                                    onChange={this.handleTournamentDetailChange} 
                                    placeholder="e.g., Bubble, Final Table"
                                  />
                                </div>
                              </div>
                              {/* Right Column */}
                              <div className="form-column">
                                <div className="form-group">
                                  <label htmlFor="levelStructure">Level Structure:</label>
                                  <textarea 
                                    id="levelStructure"
                                    name="levelStructure"
                                    rows={3}
                                    value={this.state.tournamentDetails.levelStructure}
                                    onChange={this.handleTournamentDetailChange} 
                                    placeholder="e.g., 15 min levels, blinds 1k/2k ante 2k"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="prizeStructure">Prize Structure:</label>
                                  <textarea 
                                    id="prizeStructure"
                                    name="prizeStructure"
                                    rows={3}
                                    value={this.state.tournamentDetails.prizeStructure}
                                    onChange={this.handleTournamentDetailChange} 
                                    placeholder="e.g., 1st: $1000, 9 places paid"
                                  />
                                </div>
                                <div className="form-group">
                                  <label htmlFor="payJumpInfo">Pay Jump Info:</label>
                                  <input 
                                    type="text" 
                                    id="payJumpInfo"
                                    name="payJumpInfo"
                                    value={this.state.tournamentDetails.payJumpInfo}
                                    onChange={this.handleTournamentDetailChange} 
                                    placeholder="e.g., Next jump $50 at 9 players"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* --- END Conditional Form --- */}

                      </div> {/* End player-management */}

                      {/* REMOVED Tournament Toggle and Form from here */}

                      {/* --- Desktop Timeline Grid (Hidden on Mobile by CSS) --- */}
                      <div className="streets-timeline">
                        {streets.map(street => (
                          <div key={street} className="street-header">
                            <span>{street.charAt(0).toUpperCase() + street.slice(1)}</span>
                            <span className="pot-total">${this.calculateRunningTotal(street)}</span>
                          </div>
                        ))}

                        {this.state.players.map(player => (
                          <React.Fragment key={player.id}>
                            <div
                              className={`player-label ${player.id === this.state.currentPlayerId ? 'current-player' : ''}`}
                              data-player-info={`${player.name} (${player.position})`}
                            >
                              <div>{player.name} ({player.position})</div>
                            </div>

                            {streets.map(street => {
                              const isPlayerTurn = player.id === this.state.currentPlayerId && street === this.state.currentStreet;
                              const hasFolded = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1).some(s => (player.actions?.[s] || []).some(action => action.type === 'Fold'));
                              // Add check if player is already all-in (v17)
                              const isAlreadyAllIn = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
                                  .some(s => (player.actions?.[s] || []).some(a => (a as BaseAction & { isAllIn?: boolean }).isAllIn === true));

                              return (
                                <div
                                  key={street}
                                  className={`street-cell ${hasFolded ? 'folded' : ''} ${isPlayerTurn ? 'players-turn' : ''}`}
                                >
                                  <div className="timeline-dots">
                                    {(player.actions?.[street] || []).map((action, index) => {
                                      const actionNumber = index + 1;
                                       // Determine if action is all-in (v15.1 - Cast action type)
                                       const isAllInAction = (action as BaseAction & { isAllIn?: boolean }).isAllIn === true; 
                                      return (
                                        <div key={index} className="timeline-dot">
                                          <button className="delete-action" onClick={(e) => { e.stopPropagation(); this.handleAction(player.id, { type: 'Delete', actionIndex: index }, street); }}>×</button>
                                          <span className="dot-number">{actionNumber}</span>
                                          {/* Modify label for all-in (v15) */}
                                          <span className="action-label">
                                              {action.type === 'Call' ? `Call $${action.amount}` :
                                               action.type === 'Bet' ? `Bet $${action.amount}` :
                                               action.type === 'Raise' ? `Raise $${action.amount}` :
                                               action.type}
                                              {isAllInAction ? ' (AI)' : ''} 
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {isPlayerTurn && !hasFolded && !isAlreadyAllIn && (
                                    <div className="action-buttons">
                                      {(this.state.bets?.[street] || 0) === 0 ? (
                                        <>
                                          {street !== 'preflop' && (<button className="check" onClick={() => this.handleAction(player.id, { type: 'Check' }, street)}>Check</button>)}
                                          <button className="bet" onClick={() => { this.setState({ showBetInput: true, pendingAction: { playerId: player.id, type: 'Bet', street: street } }); }}>Bet</button>
                                          {/* Remove All-in Button (v17.1) */}
                                        </>
                                      ) : (
                                        <>
                                          <button className="call" onClick={() => this.handleAction(player.id, { type: 'Call' }, street)}>Call ${this.state.bets?.[street] || 0}</button>
                                          <button className="raise" onClick={() => { this.setState({ showBetInput: true, pendingAction: { playerId: player.id, type: 'Raise', street: street } }); }}>Raise</button>
                                           {/* Remove All-in Button (v17.1) */}
                                        </>
                                      )}
                                      <button className="fold" onClick={() => this.handleAction(player.id, { type: 'Fold' }, street)}>Fold</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                      {/* --- END Desktop Timeline Grid --- */}


                      {/* --- Mobile Street-by-Street View (Shown by CSS) --- */}
                      <div className="mobile-streets-view">
                        <div className="mobile-street-tabs">
                          {streets.map(street => (
                            <button
                              key={street}
                              className={`mobile-street-tab ${this.state.mobileVisibleStreet === street ? 'active' : ''}`}
                              onClick={() => this.setState({ mobileVisibleStreet: street })}
                            >
                              {street.charAt(0).toUpperCase() + street.slice(1)}
                              <span className="pot-total">Pot: ${this.calculateRunningTotal(street)}</span>
                            </button>
                          ))}
                        </div>

                        <div className="mobile-street-content">
                          {this.state.players.map(player => {
                            const street = this.state.mobileVisibleStreet;
                            const isPlayerTurn = player.id === this.state.currentPlayerId && street === this.state.currentStreet;
                            const hasFolded = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1).some(s => (player.actions?.[s] || []).some(action => action.type === 'Fold'));
                            // Add check if player is already all-in (v17)
                            const isAlreadyAllIn = STREET_ORDER.slice(0, STREET_ORDER.indexOf(street) + 1)
                                .some(s => (player.actions?.[s] || []).some(a => (a as BaseAction & { isAllIn?: boolean }).isAllIn === true));

                            return (
                              <div key={player.id} className={`mobile-player-row ${hasFolded ? 'folded' : ''} ${isPlayerTurn ? 'current-player' : ''}`}>
                                <div className="mobile-player-info">
                                  {player.name} ({player.position})
                                </div>
                                <div className={`mobile-player-actions-area ${isPlayerTurn ? '' : 'inactive'}`}>
                                  <div className="timeline-dots">
                                    {(player.actions?.[street] || []).map((action, index) => {
                                        const actionNumber = index + 1;
                                         // Determine if action is all-in (v15.1 - Cast action type)
                                         const isAllInAction = (action as BaseAction & { isAllIn?: boolean }).isAllIn === true; 
                                        return (
                                          <div key={index} className="timeline-dot">
                                            <button className="delete-action" onClick={(e) => { e.stopPropagation(); this.handleAction(player.id, { type: 'Delete', actionIndex: index }, street); }}>×</button>
                                            <span className="dot-number">{actionNumber}</span>
                                            {/* Modify label for all-in (v15) */}
                                            <span className="action-label">
                                              {action.type === 'Call' ? `Call $${action.amount}` :
                                               action.type === 'Bet' ? `Bet $${action.amount}` :
                                               action.type === 'Raise' ? `Raise $${action.amount}` :
                                               action.type}
                                              {isAllInAction ? ' (AI)' : ''}
                                            </span>
                                          </div>
                                        );
                                    })}
                                  </div>
                                  {isPlayerTurn && !hasFolded && !isAlreadyAllIn && (
                                    <div className="action-buttons">
                                      {(this.state.bets?.[street] || 0) === 0 ? (
                                        <>
                                          {street !== 'preflop' && (
                                            <button className="check" onClick={() => this.handleAction(player.id, { type: 'Check' }, street)}>Check</button>
                                          )}
                                          <button className="bet" onClick={() => { this.setState({ showBetInput: true, pendingAction: { playerId: player.id, type: 'Bet', street: street } }); }}>Bet</button>
                                          {/* Remove All-in Button (v17.1) */}
                                        </>
                                      ) : (
                                        <>
                                          <button className="call" onClick={() => this.handleAction(player.id, { type: 'Call' }, street)}>Call ${this.state.bets?.[street] || 0}</button>
                                          <button className="raise" onClick={() => { this.setState({ showBetInput: true, pendingAction: { playerId: player.id, type: 'Raise', street: street } }); }}>Raise</button>
                                          {/* Remove All-in Button (v17.1) */}
                                        </>
                                      )}
                                      <button className="fold" onClick={() => this.handleAction(player.id, { type: 'Fold' }, street)}>Fold</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    {/* --- END streets-container --- */}

                    {/* --->>> Declare Winner Section <<<--- */}
                    <div className="declare-winner-section" style={{ marginTop: '1rem', padding: '1rem', borderTop: '1px solid #444' }}>
                        <h4 style={{ marginTop: '0', marginBottom: '1rem' }}>Declare Winner(s) / Chop</h4>
                        <div className="winner-buttons-container" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}> 
                            {/* Player Winner Buttons */}
                            {this.state.players.map(player => {
                                const isDeclaredWinner = this.state.declaredWinnerIds.includes(player.id);
                                return (
                                    <button 
                                        key={player.id}
                                        onClick={() => this.handleDeclareWinner(player.id)}
                                        className={`winner-button ${isDeclaredWinner ? 'selected' : ''}`}
                                        style={{ 
                                            margin: '5px', 
                                            padding: '8px 12px', 
                                            border: `1px solid ${isDeclaredWinner ? '#4ade80' : '#666'}`, 
                                            backgroundColor: isDeclaredWinner ? '#2f855a' : '#4a5568', 
                                            color: 'white',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {player.name} {isDeclaredWinner ? '✓' : ''}
                                    </button>
                                );
                            })}
                            {/* Chop Pot Button */}
                            <button
                                key="chop-button"
                                onClick={this.handleToggleChop}
                                className={`chop-button ${this.state.isChoppedPot ? 'selected' : ''}`}
                                style={{
                                    margin: '5px',
                                    padding: '8px 12px',
                                    border: `1px solid ${this.state.isChoppedPot ? '#f6ad55' : '#666'}`, // Different highlight for chop
                                    backgroundColor: this.state.isChoppedPot ? '#b7791f' : '#4a5568', // Different bg for chop
                                    color: 'white',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Chop Pot {this.state.isChoppedPot ? '✓' : ''}
                            </button>
                        </div>
                        <p style={{ fontSize: '0.8em', color: '#a0aec0', marginTop: '0.5rem' }}>
                            Select winner(s) or mark as a chopped pot.
                        </p>
                    </div>
                    {/* --- END Declare Winner Section --- */}

                    {/* --- Analysis Trigger and Display Section --- */}
                    <div className="analysis-controls-and-display" style={{ marginTop: '2rem' }}> 
                      {/* ADDED Clear Hand Button */}
                      <button
                        className="clear-hand-button" // Add a class for styling if needed
                        onClick={this.clearHand}
                        style={{ marginRight: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' }} // Basic styling
                      >
                        Clear Hand
                      </button>

                      {/* --- REMOVED Duplicate Session ID Input Section --- */}
                      
                      {/* ADDED Custom Instructions Textarea (SHARED STATE) */}
                      <div style={{ margin: '1rem 0' }}>
                        <label htmlFor="replayerCustomInstructions" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Optional Question/Instructions:</label>
                        <textarea
                          id="replayerCustomInstructions"
                          className="custom-instructions"
                          rows={3}
                          value={this.state.customInstructions} // Shared state
                          onChange={this.handleCustomInstructionsChange} // Shared handler
                          placeholder="Ask PokerBuddy a question!"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Updated Analyze Hand Button */}
                      <button
                        onClick={this.analyzeHand}
                        disabled={finalDisabledState} // Use the coerced boolean state
                        className="generate-summary-button" 
                      >
                        Analyze Hand
                      </button>

                      {/* --- UPDATED Confirmation & Buttons Block --- */}
                      {this.state.latestHandId && !this.state.analysisError && (
                        <>
                          {/* Confirmation Message */}
                          <p className="confirmation-message" style={{ marginTop: '1rem', color: '#4ade80', fontWeight: 'bold' }}>
                            Analysis requested for Hand <code>{this.state.latestHandId}</code>
                            {this.state.sessionId && this.state.sessionId !== 'adhoc' && ` in session ${this.state.sessionId}`}
                            {(!this.state.sessionId || this.state.sessionId === 'adhoc') && ` (Adhoc)`}.
                          </p>

                          {/* Link to Session Page (including adhoc) */}
                          <Link 
                            to={`/history/user/${PlayFab.getPlayerId()}/sessions/${this.state.sessionId || 'adhoc'}`}
                            className="action-button view-analysis-link" 
                            style={{ marginTop: '0.5rem', display: 'inline-block' }}
                          >
                            View Session
                          </Link>
                          
                          {/* Go To / Share Buttons REMOVED */}
                        </>
                      )}
                      {/* --- END Confirmation & Buttons Block --- */}

                      {/* ADDED Summary Popup Logic */}
                      {this.state.showSummary && (
                        <div className="summary-popup">
                          <button onClick={() => this.setState({ showSummary: false })}>✕</button>
                          {/* Convert JSON object to string for display */}
                          <pre>{JSON.stringify(this.generateHandSummary(Math.random().toString(36).substring(2, 7).toUpperCase()), null, 2)}</pre> 
                        </div>
                      )}

                      {/* Analysis Display Area */}
                      <div className="analysis-section" style={{ marginTop: '1rem' }}> 
                        {/* Display error if analysis failed */}
                        {this.state.analysisError && !this.state.isAnalyzing && (
                          <div className="error-message">
                            {this.state.analysisError}
                          </div>
                        )}

                        {/* Display analysis results if available */}
                        {this.state.analysis && !this.state.isAnalyzing && (
                          <AnalysisDisplay 
                            analysis={this.state.analysis} 
                            // --- Pass Improvement Props ---
                            improvementFeedback={this.state.improvementFeedback}
                            isImprovingAnalysis={this.state.isImprovingAnalysis}
                            onImprovementFeedbackChange={this.handleImprovementFeedbackChange}
                            onImproveAnalysisRequest={this.handleImproveAnalysisRequest}
                            // --- End Pass Props ---
                          />
                        )}
                        {/* Loading state is handled globally by AnalysisLoadingPopup */}
                      </div>

                      {/* --- ADD Analysis Status Display --- */}
                      <div className="analysis-status-display" style={{ marginTop: '1rem', textAlign: 'center' }}>
                        {analysisStatusLoading && (
                          <p>Loading analysis status...</p>
                        )}
                        {this.state.analysisStatusError && (
                          <p className="error-message">Error loading status: {this.state.analysisStatusError}</p>
                        )}
                        {analysisStatus && !analysisStatusLoading && !this.state.analysisStatusError && (
                          <>
                            {analysisStatus.isPremium ? (
                              <p style={{ color: 'gold' }}>Premium Account: Unlimited Analyses ✨</p>
                            ) : (
                              <>
                                <p>Free Analyses Remaining Today: {analysisStatus.remainingAnalyses ?? 'N/A'}</p>
                                {/* --- Add Subscribe Prompt/Link --- */}
                                {analysisStatus.remainingAnalyses === 0 && (
                                  <div className="subscribe-prompt" style={{ marginTop: '0.5rem' }}>
                                    <p>You've reached your daily free analysis limit.</p>
                                    <Link to="/subscribe" className="subscribe-button">
                                      Subscribe for Unlimited Analyses
                                    </Link>
                                  </div>
                                )}
                                {/* --- End Subscribe Prompt/Link --- */}
                              </>
                            )}
                          </>
                        )}
                      </div>
                      {/* --- END Analysis Status Display --- */}

                      {/* --- REMOVED Separate Confirmation Message Block --- */}
                      
                    </div>
                    {/* --- END Analysis Trigger and Display Section --- */}

                  </div> {/* --- END hand-replayer --- */}
                  
                </> /* End Fragment */
              )
            ) : this.state.activeTab === 'import' ? ( // <<< ELSE IF Import Tab >>>
              <HandHistoryImport
                initiateAnalysis={this.initiateAnalysis}
                analysis={this.state.analysis}
                isAnalyzing={this.state.isAnalyzing}
                error={this.state.analysisError}
                heroUsername={this.state.heroUsername}
                onHeroUsernameChange={this.handleHeroUsernameChange}
                customInstructions={this.state.customInstructions}
                onCustomInstructionsChange={this.handleCustomInstructionsChange}
                analysisStatusLoading={this.state.analysisStatusLoading}
                analysisStatus={this.state.analysisStatus}
                analysisStatusError={this.state.analysisStatusError}
                improvementFeedback={this.state.improvementFeedback}
                isImprovingAnalysis={this.state.isImprovingAnalysis}
                onImprovementFeedbackChange={this.handleImprovementFeedbackChange}
                onImproveAnalysisRequest={this.handleImproveAnalysisRequest}
                latestHandId={this.state.latestHandId}
                sessionId={this.state.sessionId} // Pass session ID
                // <<< Pass Online Blind Props >>>
                onlineSmallBlind={this.state.onlineSmallBlind}
                onlineBigBlind={this.state.onlineBigBlind}
                onOnlineSmallBlindChange={this.handleOnlineSmallBlindChange}
                onOnlineBigBlindChange={this.handleOnlineBigBlindChange}
              />
            ) : this.state.activeTab === 'voice' ? ( // <<< ELSE IF Voice Tab >>>
              <div className="voice-input-tab" style={{ padding: '1rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Record or Upload Hand Audio</h3>
                {/* --- ADDED Coming Soon Disclaimer --- */}
                <p style={{ fontSize: '1.1em', color: '#ffc107', /* Yellow color */ textAlign: 'center', fontWeight: 'bold', marginBottom: '1.5rem', border: '1px dashed #ffc107', padding: '0.5rem', borderRadius: '4px' }}>
                  ⚠️ Note: This feature is currently under development and coming soon! ⚠️
                </p>
                {/* --- END Coming Soon Disclaimer --- */}

                {/* Session ID and Instructions are handled globally above the tabs */}

                {/* Recording Section */}
                <div className="audio-recording-section" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #444' }}>
                  <h4 style={{ marginTop: '0', marginBottom: '1rem' }}>Option 1: Record Audio</h4>
                  <button
                    onClick={this.handleToggleRecording}
                    disabled={this.state.isUploadingAudio} // Disable if uploading
                    style={{
                      marginRight: '10px',
                      backgroundColor: this.state.isRecording ? '#dc3545' : '#28a745',
                      color: 'white',
                      border: 'none', padding: '10px 18px', borderRadius: '4px', cursor: 'pointer', minWidth: '140px'
                    }}
                  >
                    {this.state.isRecording ? 'Stop Recording' : 'Start Recording'}
                  </button>
                  {this.state.isRecording && <span style={{ color: '#ffc107', marginLeft: '10px', fontWeight: 'bold' }}>◉ Recording...</span>}
                  {this.state.recordingError && <p className="error-message" style={{ color: 'red', marginTop: '10px' }}>{this.state.recordingError}</p>}
                </div>

                {/* Upload Section */}
                <div className="audio-upload-section" style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginTop: '0', marginBottom: '1rem' }}>Option 2: Upload Audio File</h4>
                    <input
                        type="file"
                        id="audioFile"
                        accept="audio/*" // Accept any audio format
                        onChange={this.handleAudioFileSelect}
                        disabled={this.state.isRecording || this.state.isUploadingAudio} // Disable if recording or already uploading
                        // Basic styling, can be improved
                        style={{
                           display: 'block',
                           padding: '10px',
                           border: '1px solid #ccc',
                           borderRadius: '4px',
                           cursor: 'pointer'
                         }}
                    />
                    {/* Consider adding better styling for the file input */}
                </div>

                {/* Display selected/recorded file info & Submit Button */}
                {this.state.audioBlob && !this.state.isRecording && (
                    <div className="submit-audio-section" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #444' }}>
                        <p style={{ marginBottom: '0.5rem' }}>
                            Ready to analyze: <strong>{(this.state.audioBlob as File).name || 'Recorded Audio'}</strong> ({Math.round(this.state.audioBlob.size / 1024)} KB)
                        </p>
                        <button
                            onClick={this.submitAudioForAnalysis} // Hook up the handler
                            disabled={this.state.isUploadingAudio || this.state.isRecording}
                            className="generate-summary-button" // Reuse class
                            style={{ minWidth: '150px' }}
                        >
                            {this.state.isUploadingAudio ? `Uploading... ${this.state.uploadProgress ?? ''}%` : 'Upload and Analyze'}
                        </button>
                        {this.state.uploadError && <p className="error-message" style={{ color: 'red', marginTop: '10px' }}>Upload Error: {this.state.uploadError}</p>}

                        {/* Confirmation Message Area (after successful trigger) */}
                         {this.state.latestAudioHandId && !this.state.isUploadingAudio && !this.state.uploadError && (
                             <div style={{ marginTop: '1rem', color: '#4ade80', fontWeight: 'bold' }}>
                                 <p>
                                     ✅ Upload successful! Analysis triggered for Hand ID: <code>{this.state.latestAudioHandId}</code>
                                     {this.state.sessionId && this.state.sessionId !== 'adhoc' && ` in session ${this.state.sessionId}`}.
                                 </p>
                                 {/* Add Link to Session History */}
                                 <Link
                                     to={`/history/user/${PlayFab.getPlayerId()}/sessions/${this.state.sessionId || 'adhoc'}`}
                                     className="action-button view-analysis-link"
                                     style={{ marginTop: '0.5rem', display: 'inline-block' }}
                                 >
                                     View Session History
                                 </Link>
                             </div>
                         )}
                    </div>
                )}

                {/* ADDED Custom Instructions Textarea (SHARED STATE) - Redundant here, moved above tabs */}
                {/* <div style={{ marginTop: '1.5rem' }}>
                  <label htmlFor="voiceCustomInstructions" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Optional Instructions/Question:</label>
                  <textarea
                    id="voiceCustomInstructions"
                    className="custom-instructions" // Reuse class if style is suitable
                    rows={3}
                    value={this.state.customInstructions} // Shared state
                    onChange={this.handleCustomInstructionsChange} // Shared handler
                    placeholder="e.g., What could I have done differently on the turn?"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div> */}

              </div>
            ) : ( // <<< ELSE (must be Session Upload Tab) >>>
              <div className="session-upload-tab" style={{ padding: '1rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Upload Session History File</h3>

                {/* Display Session ID section (needed for upload context) */}
                <div className="session-id-input-section" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #444' }}>
                  <label htmlFor="sessionIdInputSessionUpload" style={{ marginRight: '10px', fontWeight: 'bold' }}>Session ID:</label>
                  <input
                    type="text"
                    id="sessionIdInputSessionUpload" // Unique ID for this tab
                    value={this.state.sessionId} // Shared state
                    onChange={this.handleSessionIdChange}
                    placeholder="Enter Session ID (Required for Upload)" // Indicate requirement
                    style={{ padding: '5px', marginRight: '10px', minWidth: '200px' }}
                  />
                  <p className="session-id-explanation" style={{ fontSize: '0.8em', color: '#a0aec0', marginTop: '4px' }}>
                    Required to group hands from the uploaded file. Use a descriptive name (e.g., "SundayGrind_10NL").
                  </p>
                  {this.state.lastSessionIdSuggestion && (
                    <p className="session-id-suggestion" style={{ fontSize: '0.9em', marginTop: '5px', color: '#cbd5e1' }}>
                      <i>Last used:</i> <code style={{ backgroundColor: '#4a5568', padding: '2px 4px', borderRadius: '3px', cursor: 'pointer'}} onClick={() => this.setState({ sessionId: this.state.lastSessionIdSuggestion || '' })}>{this.state.lastSessionIdSuggestion}</code>
                    </p>
                  )}
                </div>

                {/* --- Session File Upload Section --- */}
                <div className="session-file-upload-section">
                  <h4 style={{ marginTop: '0', marginBottom: '1rem' }}>Select .txt File</h4>
                  <input
                    type="file"
                    id="sessionFileInputTab"
                    accept=".txt,text/plain"
                    onChange={this.handleSessionFileSelect} // Use the handler from BuddyPage state
                    disabled={this.state.isUploadingSessionFile} // Only disable based on session upload
                    style={{
                      display: 'block',
                      marginBottom: '1rem',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />

                  {/* Display selected file info & Submit Button */} 
                  {this.state.sessionFile && (
                    <div className="submit-session-file-area" style={{ marginTop: '1rem' }}>
                      <p style={{ marginBottom: '0.5rem' }}>
                        Ready to upload: <strong>{this.state.sessionFile.name}</strong> ({Math.round(this.state.sessionFile.size / 1024)} KB)
                      </p>
                      <button
                        onClick={this.submitSessionFileForProcessing} // Use the handler from BuddyPage state
                        disabled={this.state.isUploadingSessionFile || !this.state.sessionId.trim()} // Disable during upload OR if session ID is empty
                        className="import-button" // Reuse class for similar styling
                        title={!this.state.sessionId.trim() ? "Session ID is required for upload" : "Upload session file"} // Add tooltip
                        style={{ minWidth: '150px' }}
                      >
                        {this.state.isUploadingSessionFile ? `Uploading... ${this.state.sessionFileUploadProgress ?? ''}%` : 'Upload Session File'}
                      </button>
                    </div>
                  )}

                  {/* Upload Status/Error/Confirmation */} 
                  <div className="session-upload-status" style={{ marginTop: '1rem' }}>
                    {this.state.sessionFileUploadError && <p className="error-message" style={{ color: 'red' }}>Upload Error: {this.state.sessionFileUploadError}</p>}
                    {this.state.latestSessionUploadId && !this.state.isUploadingSessionFile && !this.state.sessionFileUploadError && (
                      <div style={{ color: '#4ade80', fontWeight: 'bold' }}>
                        <p>
                          ✅ Session file uploaded successfully! Processing triggered (ID: <code>{this.state.latestSessionUploadId}</code>).
                          {this.state.sessionId && ` Added to session ${this.state.sessionId}`}.
                        </p>
                        {/* Link to session history */} 
                        {PlayFab.getPlayerId() && this.state.sessionId && (
                          <Link 
                            to={`/history/user/${PlayFab.getPlayerId()}/sessions/${this.state.sessionId}`}
                            className="action-button view-analysis-link" 
                            style={{ marginTop: '0.5rem', display: 'inline-block' }}
                          >
                            View Session History
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* --- END Session File Upload Section --- */}
              </div>
            )}
          </div>
        </div>

        {/* BetInput Popup */}
        {this.state.showBetInput && this.state.pendingAction && (() => {
          // Calculate effective stack here before rendering BetInput (v16)
          const player = this.state.players.find(p => p.id === this.state.pendingAction?.playerId);
          let currentEffectiveStack = player?.stack ?? 0;
          if (currentEffectiveStack > 0) {
              STREET_ORDER.forEach(s => {
                  if (STREET_ORDER.indexOf(s) < STREET_ORDER.indexOf(this.state.pendingAction!.street)) {
                      (player?.actions[s] || []).forEach(a => {
                          if (a.type === 'Bet' || a.type === 'Raise' || a.type === 'Call') {
                              currentEffectiveStack -= (a.amount || 0);
                          }
                      });
                  }
              });
          }
          currentEffectiveStack = Math.max(0, currentEffectiveStack);
          
          return (
            <BetInput
              onConfirm={(amount) => {
                if (this.state.pendingAction) {
                  this.handleAction(
                    this.state.pendingAction.playerId,
                    { type: this.state.pendingAction.type, amount },
                    this.state.pendingAction.street 
                  );
                  this.setState({ showBetInput: false, pendingAction: null });
                }
              }}
              onCancel={() => {
                this.setState({ showBetInput: false, pendingAction: null });
              }}
              // Pass calculated stack and new handler (v16)
              effectiveStack={currentEffectiveStack}
              onConfirmAllIn={() => {
                if (this.state.pendingAction) {
                  this.handleAction(
                    this.state.pendingAction.playerId, 
                    { 
                      type: this.state.pendingAction.type, 
                      amount: currentEffectiveStack, // Use calculated stack
                      isAllIn: true // Set the flag
                    }, 
                    this.state.pendingAction.street
                  );
                  this.setState({ showBetInput: false, pendingAction: null });
                }
              }}
            />
          );
        })()}
      </div> /* --- END page --- */
    );
  }

  // Add componentDidUpdate
  componentDidUpdate(prevProps: {}, prevState: BuddyPageState) {
    // Check if the update was triggered by a delete action
    if (this.state._lastActionWasDelete && !prevState._lastActionWasDelete) {
        console.log("[componentDidUpdate v3 - Lint Fix] Triggered after delete.");
        let latestActualActionStreetDetermined: Street = 'preflop'; 
        for (let i = STREET_ORDER.length - 1; i >= 0; i--) {
            const checkStreet = STREET_ORDER[i]; 
            if (this.state.players.some(p => (p.actions[checkStreet] || []).length > 0)) {
                latestActualActionStreetDetermined = checkStreet;
                break; 
            }
        }
        console.log(`[componentDidUpdate v3 - Lint Fix] Determined latestActualActionStreet: ${latestActualActionStreetDetermined}`);

        let lastPlayerIdOnActualStreet: number | null = null;
        const latestStreetActions: {playerId: number, index: number}[] = [];
        const maxRounds = Math.max(0, ...this.state.players.map(p => (p.actions[latestActualActionStreetDetermined] || []).length)); 
        for(let round = 0; round < maxRounds; round++) {
            getOrderedPlayers(this.state.players, latestActualActionStreetDetermined).forEach(player => { 
                const action = player.actions[latestActualActionStreetDetermined]?.[round];
                if(action) { 
                    latestStreetActions.push({playerId: player.id, index: latestStreetActions.length}); // Use player.id
                }
            });
        }
        if (latestStreetActions.length > 0) {
            lastPlayerIdOnActualStreet = latestStreetActions[latestStreetActions.length - 1].playerId;
        }
        console.log(`[componentDidUpdate v3 - Lint Fix] Last player on ${latestActualActionStreetDetermined}: ${lastPlayerIdOnActualStreet}`);

        const nextPlayerId = this.calculateNextPlayerToAct(
            this.state.players, 
            latestActualActionStreetDetermined, 
            this.state.bets, 
            lastPlayerIdOnActualStreet 
        );
        console.log(`[componentDidUpdate v3 - Lint Fix] Calculated next player: ${nextPlayerId}`);

        // Declare variables needed for the final state update
        let finalStreet: Street = latestActualActionStreetDetermined; 
        let finalPlayerId: number | null = nextPlayerId;

        if (nextPlayerId === null && finalStreet !== 'river') { 
            console.log(`[componentDidUpdate v3 - Lint Fix] End of ${finalStreet} detected after delete. Proceeding.`);
            const currentStreetIndex = STREET_ORDER.indexOf(finalStreet);
            finalStreet = STREET_ORDER[currentStreetIndex + 1];
            const finalBets = { ...this.state.bets, [finalStreet]: 0 }; 
            finalPlayerId = this.calculateNextPlayerToAct(this.state.players, finalStreet, finalBets, null); 
            console.log(`[componentDidUpdate v3 - Lint Fix] First player to act on ${finalStreet}: ${finalPlayerId}`);
        } else if (nextPlayerId === null && finalStreet === 'river') { 
            console.log("[componentDidUpdate v3 - Lint Fix] End of river detected after delete.");
        }

        this.setState({ 
            currentStreet: finalStreet, 
            mobileVisibleStreet: finalStreet, // Update mobile view to the recalculated street
            currentPlayerId: finalPlayerId, 
            _lastActionWasDelete: false 
        });
    }
  }

  // --- Add Handler for Hero Username ---
  // Definition moved up earlier
  // --- End Handler ---

  // --- ADD Handler for Custom Instructions ---
  // Definition moved up earlier
  // --- END Handler ---

  // <<< START Audio Recording Handlers >>>
  private audioChunks: Blob[] = []; // Temporary storage for audio chunks

  // Use arrow function for auto-binding
  startRecording = async () => {
    // Reset state *before* starting
    this.setState({
      isRecording: true,
      recordingError: null,
      audioBlob: null, // Clear any previous blob
      latestAudioHandId: null, // Clear previous confirmation
      isUploadingAudio: false, // Ensure upload state is reset
      uploadError: null,
      uploadProgress: null,
    });
    this.audioChunks = []; // Clear previous chunks

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Specify MIME type and potentially codecs for better compatibility
      const options = { mimeType: 'audio/webm;codecs=opus' };
      let recorder: MediaRecorder;
      try {
          recorder = new MediaRecorder(stream, options);
      } catch (e1) {
          console.warn(`MediaRecorder with ${options.mimeType} failed:`, e1, "Trying default.");
          try {
             recorder = new MediaRecorder(stream); // Fallback to default
          } catch (e2) {
              console.error("MediaRecorder failed even with default options:", e2);
              this.setState({ recordingError: "Audio recording is not supported by this browser.", isRecording: false });
              stream.getTracks().forEach(track => track.stop()); // Clean up stream
              return;
          }
      }


      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: recorder.mimeType || 'audio/webm' }); // Use the recorder's actual mimeType
        this.setState({
            audioBlob, // Set the blob here
            isRecording: false,
            mediaRecorder: null
        });
        // Stop the tracks on the stream to turn off the mic indicator
        stream.getTracks().forEach(track => track.stop());
        console.log("Recording stopped, Blob created:", audioBlob, "Type:", audioBlob.type);
      };

      recorder.onerror = (event) => {
         console.error("MediaRecorder error:", event);
         // Type assertion needed because TS doesn't know the specific error type
         const error = (event as any).error || new Error('Unknown recording error');
         this.setState({ recordingError: `Recording error: ${error.message}`, isRecording: false, mediaRecorder: null });
         stream.getTracks().forEach(track => track.stop()); // Clean up stream
      };

      this.setState({ mediaRecorder: recorder });
      recorder.start();
      console.log("Recording started with MIME type:", recorder.mimeType);

    } catch (err) {
      console.error("Error accessing microphone:", err);
      this.setState({ recordingError: "Microphone access denied or unavailable.", isRecording: false });
    }
  };

  // Use arrow function for auto-binding
  stopRecording = () => {
    if (this.state.mediaRecorder && this.state.mediaRecorder.state === 'recording') {
      this.state.mediaRecorder.stop();
      // The rest of the logic happens in recorder.onstop
    } else {
        console.warn("Stop recording called but no active recorder found.");
        this.setState({ isRecording: false }); // Ensure state consistency
    }
  };

  // Toggle function for the button - Use arrow function
  handleToggleRecording = () => {
      if (this.state.isRecording) {
          this.stopRecording();
      } else {
          this.startRecording();
      }
  };
  // <<< END Audio Recording Handlers >>>

  // <<< START Audio File Upload Handler >>>
  // Use arrow function for auto-binding
  handleAudioFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
        console.log("Audio file selected:", file);
        // Reset recording/upload state when a new file is selected
        this.setState({
            audioBlob: file,
            isRecording: false, // Stop recording if it was happening
            recordingError: null,
            latestAudioHandId: null,
            isUploadingAudio: false,
            uploadError: null,
            uploadProgress: null,
         });
        if (this.state.mediaRecorder && this.state.mediaRecorder.state === 'recording') {
            this.stopRecording(); // Stop recorder if active
        }
        // Clear the input value so the same file can be selected again if needed
        event.target.value = '';
    }
  };
  // <<< END Audio File Upload Handler >>>

  // <<< START Audio Submission Orchestrator >>>
  submitAudioForAnalysis = async () => {
    const { audioBlob, sessionId, customInstructions, heroUsername } = this.state;
    if (!audioBlob) {
        console.error("No audio blob available to submit.");
        this.setState({ uploadError: "No audio recording or file selected." });
        return;
    }

    this.setState({
        isUploadingAudio: true,
        uploadError: null,
        uploadProgress: 0, // Initialize progress
        latestAudioHandId: null, // Clear previous confirmation
        analysisError: null,     // Clear any previous general analysis error
    });

    const sessionIdToSend = sessionId.trim() || "adhoc";
    const filenameTimestamp = new Date().toISOString().replace(/[:.]/g, '-'); // Make filename safe
    // Generate a unique-ish filename for S3. Include session/user info if desired.
    // Use a default extension like .webm, the backend might rely on metadata more
    const defaultExtension = 'webm';
    const blobExtension = audioBlob.type.split('/')[1]?.split(';')[0] || defaultExtension;
    const uniqueFileName = `audio-hands/${PlayFab.getPlayerId() || 'unknown-user'}/${sessionIdToSend}/${filenameTimestamp}-${((this.state.audioBlob as File).name || `recording.${blobExtension}`).replace(/[^a-zA-Z0-9._-]/g, '_')}`;

    try {
        // Step 1: Call CloudScript to get the presigned URL
        const contentType = audioBlob.type || 'application/octet-stream';
        console.log(`Requesting upload URL for: ${uniqueFileName}, Type: ${contentType}`);
        const urlResponse = await PlayFab.executeCloudScript('getAudioUploadUrl', {
            fileName: uniqueFileName,
            contentType: contentType // Provide content type
            // Optionally pass other metadata if needed by CloudScript/Lambda
            // sessionId: sessionIdToSend,
            // customInstructions: customInstructions,
            // heroUsername: heroUsername
        });

        // --- Robust URL Response Check ---
        if (!urlResponse.success || !urlResponse.data?.FunctionResult?.success) {
            const errorMsg = urlResponse.data?.FunctionResult?.error || "Failed to get upload URL from server.";
            console.error("CloudScript getAudioUploadUrl failed:", errorMsg, urlResponse);
            throw new Error(errorMsg);
        }

        const { uploadUrl, finalKey, confirmationId } = urlResponse.data.FunctionResult;
        if (!uploadUrl || !finalKey || !confirmationId) {
             console.error("Incomplete data received from getAudioUploadUrl:", urlResponse.data.FunctionResult);
             throw new Error("Server did not provide complete upload details.");
        }
        // --- End Robust Check ---

        console.log(`Received Presigned URL: ${uploadUrl.substring(0, 100)}...`);
        console.log(`Final S3 Key: ${finalKey}`);
        console.log(`Confirmation ID for analysis: ${confirmationId}`);


        // Step 2: Upload the blob directly using XMLHttpRequest for progress
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        // Set Content-Type header exactly as used for signing
        xhr.setRequestHeader('Content-Type', contentType);

        // --- Progress Tracking ---
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = Math.round((event.loaded / event.total) * 100);
                this.setState({ uploadProgress: percentComplete });
                // console.log(`Upload Progress: ${percentComplete}%`); // Can be noisy
            }
        };
        // --- End Progress Tracking ---

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                console.log("Audio upload successful!");
                this.setState({
                    isUploadingAudio: false,
                    uploadProgress: 100,
                    audioBlob: null, // Clear blob after successful upload
                    latestAudioHandId: confirmationId, // Show confirmation using the ID from backend
                    // Optionally update lastSessionIdSuggestion here too
                    lastSessionIdSuggestion: sessionIdToSend === "adhoc" ? null : sessionIdToSend
                });
                // The backend (Lambda via S3 event) will now handle transcription and analysis.
                // The user can navigate to the session history later.
            } else {
                console.error(`Upload failed with status: ${xhr.status}`, xhr.responseText);
                this.setState({
                    isUploadingAudio: false,
                    uploadError: `Upload failed: Server responded with status ${xhr.status}. Check CORS configuration on S3 bucket.`,
                    uploadProgress: null,
                });
            }
        };

        xhr.onerror = () => {
            console.error("Upload failed due to network error or CORS issue.");
            this.setState({
                isUploadingAudio: false,
                uploadError: "Upload failed. Check network connection or CORS configuration on S3 bucket.",
                uploadProgress: null,
            });
        };

        xhr.send(audioBlob); // Send the raw blob data

    } catch (error: any) {
        console.error("Error during audio submission process:", error);
        this.setState({
            isUploadingAudio: false,
            uploadError: error.message || "An unknown error occurred during submission.",
            uploadProgress: null,
        });
    }
  };
  // <<< END Audio Submission Orchestrator >>>

  // <<< START Session File Upload Handlers >>>
  handleSessionFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/plain') {
        console.log("Session text file selected:", file);
        // Reset any previous upload state for session files
        this.setState({
            sessionFile: file,
            isUploadingSessionFile: false,
            sessionFileUploadError: null,
            sessionFileUploadProgress: null,
            latestSessionUploadId: null,
         });
        // Clear the input value so the same file can be selected again if needed
        event.target.value = '';
    } else if (file) {
        console.warn("Invalid file type selected for session upload:", file.type);
        // Optionally provide user feedback about invalid file type
        this.setState({ 
          sessionFile: null, // Clear any previously selected valid file
          sessionFileUploadError: "Invalid file type. Please select a .txt file." 
        });
        event.target.value = ''; // Clear the input
    } else {
      // Clear state if selection is cancelled
      this.setState({
            sessionFile: null,
            isUploadingSessionFile: false,
            sessionFileUploadError: null,
            sessionFileUploadProgress: null,
            latestSessionUploadId: null,
      });
    }
  };

  // Use arrow function to automatically bind 'this'
  submitSessionFileForProcessing = async () => {
    const { sessionFile, sessionId } = this.state;
    if (!sessionFile) {
        console.error("No session file available to submit.");
        this.setState({ sessionFileUploadError: "No session file selected." });
        return;
    }
    if (!sessionId.trim()) {
        console.error("Session ID is required to submit session file.");
        this.setState({ sessionFileUploadError: "Session ID is required before uploading." });
        return;
    }

    this.setState({
        isUploadingSessionFile: true,
        sessionFileUploadError: null,
        sessionFileUploadProgress: 0,
        latestSessionUploadId: null,
    });

    const sessionIdToSend = sessionId.trim();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const safeOriginalFilename = sessionFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const uniqueFileName = `session-histories/${PlayFab.getPlayerId() || 'unknown-user'}/${sessionIdToSend}/${timestamp}-${safeOriginalFilename}`;
    const contentType = 'text/plain'; // Session files are plain text

    try {
      // Step 1: Call CloudScript to get the presigned URL
      console.log(`Requesting session upload URL for: ${uniqueFileName}, Type: ${contentType}`);
      const urlResponse = await PlayFab.executeCloudScript('getSessionFileUploadUrl', {
        fileName: uniqueFileName,
        contentType: contentType
      });

      // --- Robust URL Response Check --- 
      if (!urlResponse.success || !urlResponse.data?.FunctionResult?.success) {
        const errorMsg = urlResponse.data?.FunctionResult?.error || "Failed to get session upload URL from server.";
        console.error("CloudScript getSessionFileUploadUrl failed:", errorMsg, urlResponse);
        throw new Error(errorMsg);
      }

      const { uploadUrl, finalKey, confirmationId } = urlResponse.data.FunctionResult;
      if (!uploadUrl || !finalKey || !confirmationId) {
          console.error("Incomplete data received from getSessionFileUploadUrl:", urlResponse.data.FunctionResult);
          throw new Error("Server did not provide complete upload details.");
      }
      // --- End Robust Check ---

      console.log(`Received Presigned URL for session file: ${uploadUrl.substring(0, 100)}...`);
      console.log(`Final S3 Key: ${finalKey}`);
      console.log(`Confirmation ID for session processing: ${confirmationId}`);

      // Step 2: Upload the file directly using XMLHttpRequest for progress
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);

      // --- Progress Tracking ---
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          this.setState({ sessionFileUploadProgress: percentComplete });
        }
      };
      // --- End Progress Tracking ---

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log("Session file upload successful!");
          this.setState({
            isUploadingSessionFile: false,
            sessionFileUploadProgress: 100,
            sessionFile: null, // Clear file after successful upload
            latestSessionUploadId: confirmationId, // Show confirmation using the ID from backend
            lastSessionIdSuggestion: sessionIdToSend // Update suggestion
          });
          // Backend S3 trigger/Lambda will handle processing the file.
        } else {
          console.error(`Session file upload failed with status: ${xhr.status}`, xhr.responseText);
          this.setState({
            isUploadingSessionFile: false,
            sessionFileUploadError: `Upload failed: Server responded with status ${xhr.status}. Check CORS.`,
            sessionFileUploadProgress: null,
          });
        }
      };

      xhr.onerror = () => {
        console.error("Session file upload failed due to network error or CORS issue.");
        this.setState({
          isUploadingSessionFile: false,
          sessionFileUploadError: "Upload failed. Check network connection or CORS configuration on S3 bucket.",
          sessionFileUploadProgress: null,
        });
      };

      xhr.send(sessionFile); // Send the actual File object

    } catch (error: any) {
      console.error("Error during session file submission process:", error);
      this.setState({
        isUploadingSessionFile: false,
        sessionFileUploadError: error.message || "An unknown error occurred during submission.",
        sessionFileUploadProgress: null,
      });
    }
  };
  // <<< END Session File Upload Handlers >>>
}

export default BuddyPage;

// --- ADDED: Explicit exports for types used elsewhere ---
export type { Position, Street, ActionType };

// Explicitly export types that PokerHand.tsx imports, just in case
// REMOVED: export type { HandSummaryActionWithPlayerId, HandSummaryPlayer };